import axios, { AxiosInstance, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  User,
  MarkupCatalog,
  CalculatedPrice,
  TelemedNetwork,
  SupervisorApiTokenView
} from '@bloom/shared';
import { LabResultDetail, LabResultSummary } from '../types/labResults';
import { checkForDeprecationHeaders } from './deprecation';
import { normalizePagination } from './pagination';
import type { ServerPage } from '../hooks/useServerList';

/**
 * A state license a physician holds. `source` records where the entry came
 * from — an NPPES registry import or the physician's own declaration — and is
 * set server-side, never by the client.
 */
export interface PhysicianLicense {
  state: string;
  licenseNumber?: string;
  expiresAt?: string;
  source?: 'nppes' | 'self';
}

export interface HierarchyMoveResult {
  subjectId: string;
  fromParentId?: string;
  toParentId: string;
  catalogsCreated: string[];
  catalogsDetached: string[];
  productCount: number;
  auditLogId: string;
}

/**
 * Shapes for the Users directory. Declared here rather than in @bloom/shared
 * deliberately: that package is linked inconsistently across installs (symlink
 * in some, a copied dist in others), so adding to it means a rebuild — and
 * sometimes a manual copy — before the frontend can compile.
 */
export type AccountState =
  | 'suspended' | 'needs_approval' | 'awaiting_network'
  | 'awaiting_id_review' | 'onboarding' | 'never_logged_in' | 'active';

export interface DirectoryBlocker {
  code: string;
  label: string;
  action: string;
  owner: 'admin' | 'user';
  since?: string;
}

// Admin "All Patients" (GET /api/admin/patients). Patients are per-clinic
// records; otherClinicRecords counts the same phone number at other clinics.
export interface AdminPatientClinic {
  _id: string;
  clinicName: string;
  physicianName: string;
  email: string;
  status: string;
  accountManager: { _id: string; name: string } | null;
}

export interface AdminPatientRow {
  _id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
  dateOfBirth: string;
  status: string;
  city: string | null;
  state: string | null;
  patientType: string;
  signupSource: string | null;
  createdAt: string;
  clinic: AdminPatientClinic | null;
  orderCount: number;
  lastOrderAt: string | null;
  otherClinicRecords: number;
}

export interface AdminPatientSearchResponse {
  rows: AdminPatientRow[];
  engine: 'atlas' | 'regex' | 'reference' | 'browse';
  pagination: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean };
}

export interface AdminPatientDetail {
  patient: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string;
    phoneE164: string | null;
    dateOfBirth: string;
    sex: string | null;
    status: string;
    address: { street: string; street2?: string; city: string; state: string; zipCode: string; country?: string };
    patientType: string;
    animal: { name: string; species: string; breed?: string } | null;
    signupSource: string | null;
    screeningAnswers: Record<string, any> | null;
    screeningAnswersUpdatedAt: string | null;
    notificationPrefs: Record<string, any> | null;
    createdAt: string;
    updatedAt: string;
  };
  clinic: AdminPatientClinic | null;
  portalAccount: { exists: boolean; passwordSet: boolean; passkeyCount: number; locked: boolean; createdAt: string | null };
  orders: Array<{
    _id: string;
    orderNumber: string;
    status: string;
    totalAmount?: number;
    telemedNetworkFee?: number;
    items: Array<{ productName: string; quantity: number }>;
    paymentDetails?: { status?: string; method?: string };
    invoiceId?: string;
    trackingNumber?: string;
    createdAt: string;
  }>;
  invoices: Array<{
    _id: string;
    invoiceNumber: string;
    status: string;
    totalAmount: number;
    amountPaid: number;
    amountDue: number;
    telemedNetworkFee?: number;
    createdAt: string;
    paidAt?: string;
  }>;
  labOrders: Array<{ _id: string; clientOrderNumber: string; panelCode: string; requisitionType: string; status: string; submittedAt?: string; resultedAt?: string; createdAt: string }>;
  memberships: Array<{ _id: string; tier: string; productCount: number; pricePerCycleCents: number; status: string; nextBillingDate?: string; createdAt: string }>;
  otherClinicRecords: Array<{ _id: string; firstName: string; lastName: string; status: string; createdAt: string; clinic: AdminPatientClinic | null }>;
}

export interface DirectoryRow {
  id: string;
  name: string;
  email: string;
  role: string;
  clinicName?: string;
  phone?: string;
  status: string;
  state: AccountState;
  blockers: DirectoryBlocker[];
  am: { id: string | null; name: string; isHouse: boolean };
  supervisor: { id: string; name: string } | null;
  catalogs: Array<{ id: string; name: string }>;
  provenance: {
    registrationMethod?: string;
    invitedByName?: string;
    referralSource?: string;
    createdAt?: string;
  };
  lastLoginAt?: string | null;
  /** Account type: what this account structurally is. */
  flags: { isPharmacy: boolean; isVet: boolean; telemedOnly: boolean };
  /** Trailing-30-day orders; percentile is null when there is no volume. */
  volume30d: { orders: number; percentile: number | null };
  /** Present in hierarchy mode: AMs under a supervisor, clinics under an AM. */
  children?: DirectoryRow[];
}

export interface DirectoryResponse {
  rows: DirectoryRow[];
  total: number;
  page: number;
  limit: number;
  facets: {
    states: Record<string, number>;
    blockers: Record<string, number>;
    roles: Record<string, number>;
  };
}

export interface UserContextResponse {
  user: DirectoryRow;
  chain: Array<{
    level: 'supervisor' | 'am' | 'self';
    id: string;
    name: string;
    role: string;
    catalogs: Array<{ id: string; name: string }>;
  }>;
}

class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '/api';
    
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for token refresh and deprecation detection
    this.client.interceptors.response.use(
      (response) => {
        // Check for deprecation headers on all successful responses
        checkForDeprecationHeaders(response);
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Don't retry refresh token calls to prevent infinite loops
        if (error.response?.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url?.includes('/auth/refresh')) {
          originalRequest._retry = true;

          try {
            const refreshToken = this.getRefreshToken();
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              if (response.success && response.data) {
                this.setToken(response.data.token);
                originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
                return this.client(originalRequest);
              }
            }
            // No refresh token or refresh returned no data - force logout
            this.forceLogout();
            return Promise.reject(error);
          } catch (refreshError) {
            // Refresh failed, force logout
            this.forceLogout();
            return Promise.reject(error);
          }
        }

        // If refresh token call itself fails, force logout
        if (error.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
          this.forceLogout();
        }

        // Capture API errors to Sentry (except 401s and known expected errors)
        if (error.response?.status !== 401) {
          const endpoint = originalRequest?.url;
          const status = error.response?.status;

          // Skip Sentry for expected permission/validation errors that aren't bugs
          const data = error.response?.data as any;
          const isExpectedError =
            // Physician not yet verified: gated endpoints (e.g. /patients,
            // /patients/stats) correctly return 403 verificationRequired. Expected
            // during onboarding — not a bug, and by far the loudest noise source.
            (status === 403 && data?.verificationRequired === true) ||
            // Rate limiting is expected and transient; the client backs off/retries.
            status === 429 ||
            (status === 403 && endpoint?.includes('/analytics/physician')) ||
            (status === 400 && endpoint?.includes('/impersonation/stop')) ||
            (status === 400 && endpoint?.includes('/auth/complete-registration')) ||
            // Background telemetry: /activity/track/batch is a fire-and-forget buffer
            // flush (useActivityTracker) that already retries silently. Its failures —
            // most often a 10s timeout with no response — are invisible to the user and
            // pure Sentry noise. Suppress this endpoint specifically; timeouts on real
            // user-facing endpoints are NOT suppressed, so they still surface as signal.
            endpoint?.includes('/activity/track');

          if (!isExpectedError) {
            Sentry.captureException(error, {
              tags: {
                'api.endpoint': endpoint,
                'api.method': originalRequest?.method?.toUpperCase(),
                'api.status': status
              },
              extra: {
                requestUrl: endpoint,
                responseStatus: status,
                responseData: error.response?.data
              }
            });
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Force logout - clears all auth state and redirects to login
  private forceLogout(): void {
    this.clearTokens();
    // Clear Zustand persisted auth state
    localStorage.removeItem('bloom-auth');
    // Redirect to login
    window.location.href = '/login';
  }

  // Token management
  private getToken(): string | null {
    return localStorage.getItem('bloom_token');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('bloom_refresh_token');
  }

  private setToken(token: string): void {
    localStorage.setItem('bloom_token', token);
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem('bloom_refresh_token', token);
  }

  private clearTokens(): void {
    localStorage.removeItem('bloom_token');
    localStorage.removeItem('bloom_refresh_token');
  }

  // Authentication endpoints
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.client.post('/auth/login', credentials);
      
      if (response.data.success && response.data.data) {
        this.setToken(response.data.data.token);
        this.setRefreshToken(response.data.data.refreshToken);
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  }

  async logout(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.client.post('/auth/logout');
      this.clearTokens();
      return response.data;
    } catch (error: any) {
      // Clear tokens even if logout request fails
      this.clearTokens();
      return {
        success: false,
        error: error.response?.data?.error || 'Logout failed'
      };
    }
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.client.get('/auth/me');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get user'
      };
    }
  }

  async validateToken(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.client.get('/auth/validate');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Token validation failed'
      };
    }
  }

  private async refreshToken(refreshToken: string): Promise<ApiResponse<{ token: string }>> {
    try {
      // Use a separate axios instance to avoid interceptor loops
      const refreshClient = axios.create({
        baseURL: this.baseURL,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const response: AxiosResponse<ApiResponse<{ token: string }>> = await refreshClient.post('/auth/refresh', {
        refreshToken
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Token refresh failed'
      };
    }
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.client.get('/health');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Health check failed'
      };
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Get current token for manual use
  getCurrentToken(): string | null {
    return this.getToken();
  }

  // Generic HTTP methods
  async get<T = any>(url: string, config?: { signal?: AbortSignal }): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  // What is still live at the pharmacy for an invoice, so the refund modal can
  // ask about stopping the fill only when there is something to stop.
  async invoicePharmacyCancellationPreview(invoiceId: string): Promise<{
    success: boolean;
    data?: {
      targets: Array<{
        kind: 'order' | 'bulk_order';
        displayId: string;
        status: string;
        pharmacyOrderIds: string[];
        value: number;
      }>;
      pharmacyOrderCount: number;
      value: number;
    };
    error?: string;
  }> {
    try {
      return await this.get<any>(`/admin/invoices/${invoiceId}/pharmacy-cancellation-preview`);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  // Admin invoice refunds (admin-only, gated on adminData.canIssueRefunds)
  async refundInvoice(invoiceId: string, body: {
    reason: string;
    amount?: number;
    orderDisposition: 'cancel' | 'keep_active';
    cancelAtPharmacy?: boolean;
  }): Promise<{
    success: boolean;
    invoice?: any;
    refund?: { id: string; status: string };
    creditMemoId?: string;
    code?: string;
    error?: string;
  }> {
    try {
      const res = await this.post<any>(`/admin/invoices/${invoiceId}/refund`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async updateAdminPermissions(userId: string, body: { canIssueRefunds?: boolean; canRecordPayment?: boolean; canMoveHierarchy?: boolean; canCompOrders?: boolean; canManageTelemedNetworks?: boolean }): Promise<{
    success: boolean;
    adminData?: { canIssueRefunds?: boolean; canRecordPayment?: boolean; canMoveHierarchy?: boolean; canCompOrders?: boolean; canManageTelemedNetworks?: boolean };
    code?: string;
    error?: string;
  }> {
    try {
      const res = await this.patch<any>(`/users/${userId}/admin-permissions`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  // In-flight order edits (address, items)
  async editOrderAddress(
    orderId: string,
    body: {
      address: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
        skipValidation?: boolean;
        note?: string;
        force?: boolean;
      };
      reason?: string;
    }
  ): Promise<{
    success: boolean;
    data?: any;
    message?: string;
    pendingPharmacyReview?: boolean;
    code?: string;
    error?: string;
    requiresForce?: boolean;
    currentStatus?: string;
    openRequestId?: number;
  }> {
    try {
      const res = await this.patch<any>(`/orders/${orderId}/address`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async switchOrderDeliveryMode(
    orderId: string,
    body: {
      targetMode: 'pickup' | 'ship';
      reason: string;
      shippingAddress?: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
      };
    }
  ): Promise<{
    success: boolean;
    data?: any;
    message?: string;
    pendingPharmacyReview?: boolean;
    code?: string;
    error?: string;
    openRequestId?: number;
  }> {
    try {
      const res = await this.patch<any>(`/orders/${orderId}/delivery-mode`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  /** Delay a submitted order at the pharmacy (clinics: only before filling starts). */
  async holdOrder(
    orderId: string,
    body: { holdUntil: string; reason: string }
  ): Promise<{ success: boolean; data?: any; message?: string; pendingPharmacyReview?: boolean; code?: string; error?: string }> {
    try {
      return await this.post<any>(`/orders/${orderId}/hold`, body);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async releaseOrderHold(
    orderId: string,
    reason?: string
  ): Promise<{ success: boolean; data?: any; message?: string; code?: string; error?: string }> {
    try {
      return await this.post<any>(`/orders/${orderId}/hold/release`, reason ? { reason } : {});
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async switchBulkOrderDeliveryMode(
    bulkOrderId: string,
    body: {
      targetMode: 'pickup' | 'ship';
      reason: string;
      shippingAddress?: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
      };
    }
  ): Promise<{
    success: boolean;
    data?: any;
    message?: string;
    pendingPharmacyReview?: boolean;
    partial?: boolean;
    subOrders?: Array<{ pharmacyOrderId: string; outcome: string; requestId?: number; message?: string }>;
    code?: string;
    error?: string;
  }> {
    try {
      const res = await this.patch<any>(`/bulk-orders/${bulkOrderId}/delivery-mode`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async editBulkOrderAddress(
    bulkOrderId: string,
    body: {
      address: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
      };
      reason?: string;
    }
  ): Promise<{
    success: boolean;
    data?: any;
    message?: string;
    requiresManualPharmacySync?: boolean;
    code?: string;
    error?: string;
  }> {
    try {
      const res = await this.patch<any>(`/bulk-orders/${bulkOrderId}/address`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async addOrderItems(
    orderId: string,
    body: {
      adds: Array<{ productId: string; quantity: number; selectedVariants?: Record<string, string> }>;
      reason?: string;
    }
  ): Promise<{
    success: boolean;
    data?: { parent: any; child: any; childInvoice: any; childPharmacyOrderId?: string; childSubmitError?: string };
    message?: string;
    code?: string;
    error?: string;
  }> {
    try {
      const res = await this.patch<any>(`/orders/${orderId}/items`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async addBulkOrderItems(
    bulkOrderId: string,
    body: {
      adds: Array<{ productId: string; quantity: number }>;
      reason?: string;
    }
  ): Promise<{
    success: boolean;
    data?: { parent: any; child: any; childInvoice: any; childPharmacyOrderIds?: string[]; childSubmitError?: string };
    message?: string;
    code?: string;
    error?: string;
  }> {
    try {
      const res = await this.patch<any>(`/bulk-orders/${bulkOrderId}/items`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  async removeOrderItems(
    orderId: string,
    body: {
      removes?: number[];
      qtyChanges?: Array<{ index: number; newQuantity: number }>;
      reason?: string;
    }
  ): Promise<{
    success: boolean;
    data?: { order: any; creditMemos?: any[]; unmatched?: any[]; memoErrors?: string[]; pharmacyRevisionResult?: any };
    message?: string;
    code?: string;
    error?: string;
  }> {
    try {
      const res = await this.patch<any>(`/orders/${orderId}/items/remove`, body);
      return res;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data.success === 'boolean') return data;
      throw err;
    }
  }

  // Product management endpoints
  async updateProductBasePrice(productId: string, basePrice: number): Promise<ApiResponse> {
    try {
      const response = await this.put<ApiResponse>(`/products/${productId}`, { basePrice });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update product base price'
      };
    }
  }

  async disableProduct(productId: string): Promise<ApiResponse> {
    try {
      const response = await this.delete<ApiResponse>(`/products/${productId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to disable product'
      };
    }
  }

  // Pricing endpoints
  async getUserCatalogs(): Promise<ApiResponse<MarkupCatalog[]>> {
    try {
      const response = await this.get<ApiResponse<MarkupCatalog[]>>('/pricing/catalogs/me');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get catalogs'
      };
    }
  }

  async getCatalog(catalogId: string): Promise<ApiResponse<MarkupCatalog>> {
    try {
      const response = await this.get<ApiResponse<MarkupCatalog>>(`/pricing/catalogs/${catalogId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get catalog'
      };
    }
  }



  async setProductMarkup(
    catalogId: string,
    productId: string,
    markupPercentage: number
  ): Promise<ApiResponse<MarkupCatalog>> {
    try {
      const response = await this.put<ApiResponse<MarkupCatalog>>(
        `/pricing/catalogs/${catalogId}/products/${productId}`,
        { markupPercentage }
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to set product markup'
      };
    }
  }

  async removeProductMarkup(catalogId: string, productId: string): Promise<ApiResponse<MarkupCatalog>> {
    try {
      const response = await this.delete<ApiResponse<MarkupCatalog>>(
        `/pricing/catalogs/${catalogId}/products/${productId}`
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove product markup'
      };
    }
  }

  async calculatePrice(productId: string): Promise<ApiResponse<CalculatedPrice>> {
    try {
      const response = await this.get<ApiResponse<CalculatedPrice>>(`/pricing/calculate/${productId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to calculate price'
      };
    }
  }

  async calculatePrices(productIds: string[]): Promise<ApiResponse<CalculatedPrice[]>> {
    try {
      const response = await this.post<ApiResponse<CalculatedPrice[]>>('/pricing/calculate-bulk', { productIds });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to calculate prices'
      };
    }
  }

  async getPhysicianCatalog(physicianId: string): Promise<ApiResponse<MarkupCatalog>> {
    try {
      const response = await this.get<ApiResponse<MarkupCatalog>>(`/pricing/catalogs/user/${physicianId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician catalog'
      };
    }
  }

  async getDistributorManagedCatalogs(): Promise<ApiResponse<{
    distributorCatalog: MarkupCatalog;
    physicianCatalogs: MarkupCatalog[];
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        distributorCatalog: MarkupCatalog;
        physicianCatalogs: MarkupCatalog[];
      }>>('/pricing/catalogs/managed');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get managed catalogs'
      };
    }
  }

  // New Catalog Management API endpoints
  
  // Admin Template Management
  async getTemplates(params?: { page?: number; limit?: number; search?: string; isActive?: boolean }): Promise<ApiResponse<{
    templates: MarkupCatalog[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
      
      const url = `/catalogs/templates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.get<ApiResponse<{
        templates: MarkupCatalog[];
        pagination: { page: number; limit: number; total: number; pages: number };
      }>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get templates'
      };
    }
  }

  /** One page of catalog templates, searched on the server (for pickers via AsyncSearchSelect). */
  async searchTemplatesPage(params: { search?: string; page?: number; pageSize?: number; isActive?: boolean; signal?: AbortSignal } = {}): Promise<ServerPage<MarkupCatalog>> {
    const page = params.page ?? 1;
    const pageSize = Math.min(params.pageSize ?? 25, 100);
    const query = new URLSearchParams({ page: String(page), limit: String(pageSize) });
    if (params.search?.trim()) query.set('search', params.search.trim());
    if (params.isActive !== undefined) query.set('isActive', String(params.isActive));
    const response = await this.get<any>(`/catalogs/templates?${query}`, { signal: params.signal });
    if (response?.success === false) throw new Error(response.error || 'Failed to load catalogs');
    const items: MarkupCatalog[] = response?.data?.templates ?? [];
    const pagination = normalizePagination(response?.data?.pagination, { page, pageSize, itemCount: items.length });
    return { items, total: pagination.total, hasMore: pagination.hasMore };
  }

  async createTemplate(data: {
    catalogName: string;
    description?: string;
    products: Array<{ productId: string; price: number; notes?: string }>;
    assignedTo?: string[];
    tags?: string[];
    catalogType?: 'cost' | 'msrp';
  }): Promise<ApiResponse<MarkupCatalog>> {
    try {
      const response = await this.post<ApiResponse<MarkupCatalog>>('/catalogs/templates', data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create template'
      };
    }
  }

  // New generic update catalog for both templates and regular catalogs
  async updateCatalog(catalogId: string, data: {
    catalogName?: string;
    description?: string;
    products?: Array<{ productId: string; price: number; notes?: string }>;
    assignedTo?: string[];
    tags?: string[];
    isActive?: boolean;
    catalogType?: 'cost' | 'msrp';
  }): Promise<ApiResponse<MarkupCatalog>> {
    try {
      const response = await this.put<ApiResponse<MarkupCatalog>>(`/catalogs/${catalogId}`, data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update catalog'
      };
    }
  }

  // Create any catalog (AM creates for physicians)
  async createCatalog(data: {
    catalogName: string;
    description?: string;
    products: Array<{ productId: string; price: number; notes?: string }>;
    assignedTo?: string[];
    tags?: string[];
  }): Promise<ApiResponse<MarkupCatalog>> {
    try {
      const response = await this.post<ApiResponse<MarkupCatalog>>('/catalogs', data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create catalog'
      };
    }
  }

  // Get my aggregated products from all assigned catalogs
  async getMyProducts(): Promise<ApiResponse<{
    products: Array<{
      _id: string;
      drugName: string;
      concentration: string;
      format: string;
      size: string;
      displayName: string;
      price: number;
      fromCatalogs: Array<{ catalogId: string; catalogName: string; price: number }>;
    }>;
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>('/products/my-products');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get my products'
      };
    }
  }

  // Get catalogs assigned to current user
  async getAssignedCatalogs(): Promise<ApiResponse<{ catalogs: MarkupCatalog[] }>> {
    try {
      const response = await this.get<ApiResponse<{ catalogs: MarkupCatalog[] }>>('/catalogs/assigned');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get assigned catalogs'
      };
    }
  }

  // Get MSRP template catalogs assigned to current user
  async getMsrpTemplates(): Promise<ApiResponse<{ templates: any[] }>> {
    try {
      const response = await this.get<ApiResponse<{ templates: any[] }>>('/catalogs/msrp-templates');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch MSRP templates'
      };
    }
  }

  // Get AM's available products from assigned catalogs (their product collection)
  async getMyProductCollection(params?: {
    page?: number;
    limit?: number;
    search?: string;
    excludeIds?: string;
  }): Promise<ApiResponse<{ products: any[], pagination: any }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.excludeIds) queryParams.append('excludeIds', params.excludeIds);
      
      const url = queryParams.toString() ? `/catalogs/my-product-collection?${queryParams.toString()}` : '/catalogs/my-product-collection';
      const response = await this.get<ApiResponse<{ products: any[], pagination: any }>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get product collection'
      };
    }
  }

  // Legacy updateTemplate for backward compatibility
  async updateTemplate(templateId: string, data: any): Promise<ApiResponse<MarkupCatalog>> {
    return this.updateCatalog(templateId, data);
  }

  async assignTemplateToAM(data: {
    templateCatalogId: string;
    accountManagerId: string;
    catalogName?: string;
  }): Promise<ApiResponse<{ catalog: MarkupCatalog; message: string }>> {
    try {
      // Map frontend field names to backend expected names
      const requestData = {
        templateCatalogId: data.templateCatalogId,
        amUserId: data.accountManagerId,
        customCatalogName: data.catalogName
      };
      const response = await this.post<ApiResponse<{ catalog: MarkupCatalog; message: string }>>('/catalogs/assign-to-am', requestData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign template to AM'
      };
    }
  }

  async getTemplateAssignments(templateId: string): Promise<ApiResponse<{ 
    templateId: string; 
    assignedDistributors: User[]; 
    assignmentCount: number 
  }>> {
    try {
      const response = await this.get<ApiResponse<{ 
        templateId: string; 
        assignedDistributors: User[]; 
        assignmentCount: number 
      }>>(`/catalogs/templates/${templateId}/assignments`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get template assignments'
      };
    }
  }

  async deleteCatalog(catalogId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.delete<ApiResponse<{ message: string }>>(`/catalogs/${catalogId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete catalog'
      };
    }
  }



  // Legacy createPhysicianCatalog and updatePhysicianCatalog methods removed
  // New system uses direct catalog assignment via assignedTo[] arrays





  async assignCatalogToPhysician(data: {
    catalogId: string;
    physicianId: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/catalogs/assign-to-physician', data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign catalog to physician'
      };
    }
  }

  async getMyAssignedCatalogs(): Promise<ApiResponse<{ catalogs: any[] }>> {
    try {
      const response = await this.get<ApiResponse<{ catalogs: any[] }>>('/catalogs/my-assigned');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get assigned catalogs'
      };
    }
  }

  async getCatalogProducts(catalogId: string): Promise<ApiResponse<{ products: any[] }>> {
    try {
      const response = await this.get<ApiResponse<{ products: any[] }>>(`/catalogs/${catalogId}/products`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get catalog products'
      };
    }
  }

  // Pricing Calculation Engine
  async calculateFullPricing(productId: string, physicianId: string): Promise<ApiResponse<{
    product: any;
    physician: any;
    pricing: {
      basePrice: number;
      adminMarkup: number;
      adminMarkupPrice: number;
      
      physicianMarkup: number;
      physicianMarkupPrice: number;
      finalPrice: number;
    };
    calculatedAt: string;
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        product: any;
        physician: any;
        pricing: {
          basePrice: number;
          adminMarkup: number;
          adminMarkupPrice: number;
          
          physicianMarkup: number;
          physicianMarkupPrice: number;
          finalPrice: number;
        };
        calculatedAt: string;
      }>>(`/pricing/calculate?productId=${productId}&physicianId=${physicianId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to calculate full pricing'
      };
    }
  }

  async getDistributorPricing(): Promise<ApiResponse<{
    products: Array<{
      _id: string;
      drugName: string;
      concentration: string;
      format: string;
      size: string;
      displayName: string;
      yourCost?: number;
      calculatedPrice?: {
        productId: string;
        basePrice: number;
        distributorPrice: number;
        finalPrice: number;
        distributorMarkup: number;
        physicianMarkup: number;
        breakdown: {
          distributorMarkupPercentage: number;
          physicianMarkupPercentage: number;
        };
      };
    }>;
    distributorId: string;
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        products: Array<{
          _id: string;
          drugName: string;
          concentration: string;
          format: string;
          size: string;
          displayName: string;
          calculatedPrice?: {
            productId: string;
            basePrice: number;
            distributorPrice: number;
            finalPrice: number;
            distributorMarkup: number;
            physicianMarkup: number;
            breakdown: {
              distributorMarkupPercentage: number;
              physicianMarkupPercentage: number;
            };
          };
        }>;
        distributorId: string;
      }>>(`/products/distributor/pricing`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get distributor pricing'
      };
    }
  }

  // Admin views of user pricing/access
  async adminViewDistributorPricing(distributorId: string): Promise<ApiResponse<{ role: string; userId: string; templateCatalogName?: string; amCatalogName?: string; products: any[] }>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/admin/users/${distributorId}/pricing/distributor`);
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch distributor pricing (admin view)' } as any;
    }
  }

  async adminViewPhysicianPricing(physicianId: string): Promise<ApiResponse<{ role: string; userId: string; catalogs: any[]; products: any[] }>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/admin/users/${physicianId}/pricing/physician`);
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch physician pricing (admin view)' } as any;
    }
  }

  async adminViewSupervisorPricing(supervisorId: string): Promise<ApiResponse<{ role: string; userId: string; catalogs: any[]; products: any[] }>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/admin/users/${supervisorId}/pricing/supervisor`);
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch supervisor pricing (admin view)' } as any;
    }
  }

  /**
   * Admin: move a physician under a different AM. Requires reason (5–500 chars).
   * Backend validates that the new AM carries every product the physician sells
   * and auto-creates a preservation catalog under the new AM.
   */
  async adminReassignPhysician(
    physicianId: string,
    newDistributorId: string,
    reason: string
  ): Promise<ApiResponse<HierarchyMoveResult> & {
    code?: string;
    missingProductIds?: string[];
    missingProducts?: Array<{ _id: string; name: string }>;
  }> {
    try {
      const response = await this.put<ApiResponse<HierarchyMoveResult>>(`/users/admin/${physicianId}/reassign`, {
        newDistributorId,
        reason
      });
      return response as any;
    } catch (error: any) {
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.error || 'Failed to move physician',
        code: data.code,
        missingProductIds: data.missingProductIds,
        missingProducts: data.missingProducts
      } as any;
    }
  }

  /**
   * Admin: move an AM under a different supervisor. Admin-direct AMs (no
   * supervisor today) are supported — that move also converts their chain
   * from 2-tier to 3-tier.
   */
  async adminMoveAmSupervisor(
    amId: string,
    newSupervisorId: string,
    reason: string
  ): Promise<ApiResponse<HierarchyMoveResult> & {
    code?: string;
    missingProductIds?: string[];
    missingProducts?: Array<{ _id: string; name: string }>;
  }> {
    try {
      const response = await this.put<ApiResponse<HierarchyMoveResult>>(`/users/admin/${amId}/move-supervisor`, {
        newSupervisorId,
        reason
      });
      return response as any;
    } catch (error: any) {
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.error || 'Failed to move AM',
        code: data.code,
        missingProductIds: data.missingProductIds,
        missingProducts: data.missingProducts
      } as any;
    }
  }

  /**
   * Admin: preflight a move — returns whether it would be blocked and by which
   * products. UI uses this to render a meaningful confirm/error dialog without
   * round-tripping a failed move.
   */
  async adminMovePreflight(
    userId: string,
    toParentId: string
  ): Promise<ApiResponse<{
    canMove: boolean;
    missingProductIds: string[];
    missingProducts: Array<{ _id: string; name: string }>;
    /**
     * Products the new parent carries but pays more for than the subject will
     * after the move. Advisory, not a blocker: canMove ignores these, but the
     * parent tier would sit on a negative markup until someone reprices.
     */
    costFloorConflicts: Array<{
      _id: string;
      name: string;
      childPrice: number;
      parentCost: number;
    }>;
    productCount: number;
    fromParentId?: string;
    toParentId: string;
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>(
        `/users/admin/${userId}/move-preflight?to=${encodeURIComponent(toParentId)}`
      );
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Preflight failed' } as any;
    }
  }

  async getPhysicianPricing(physicianId: string): Promise<ApiResponse<{
    physician: any;
    catalog: any;
    products: Array<{
      _id: string;
      drugName: string;
      concentration: string;
      format: string;
      size: string;
      displayName: string;
      yourCost?: number;
      amCost?: number;
      endUserPrice?: number;
      profit?: number;
      basePrice?: number;
      adminMarkup?: number;
    
      physicianMarkup?: number;
      finalPrice?: number;
    }>;
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        physician: any;
        catalog: any;
        products: Array<{
          _id: string;
          drugName: string;
          concentration: string;
          format: string;
          size: string;
          displayName: string;
          yourCost?: number;
          amCost?: number;
          endUserPrice?: number;
          profit?: number;
          basePrice?: number;
          adminMarkup?: number;
        
          physicianMarkup?: number;
          finalPrice?: number;
        }>;
      }>>(`/products/physician/${physicianId}/pricing`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician pricing'
      };
    }
  }

  /**
   * The single Users directory: every account with a derived account state, the
   * blockers behind it, its AM (or House), assigned catalogs and provenance.
   *
   * Additive to the role-scoped endpoints — /users/physicians and friends still
   * back the existing pages.
   */
  async getUserDirectory(params: {
    q?: string;
    role?: string;
    state?: string;
    blocker?: string;
    amId?: string;
    supervisorId?: string;
    needsAdmin?: boolean;
    sort?: string;
    group?: 'flat' | 'hierarchy';
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<DirectoryResponse>> {
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') qs.append(k, String(v));
      });
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return await this.get<ApiResponse<DirectoryResponse>>(`/users/directory${suffix}`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load users'
      };
    }
  }

  /** The org chain above one user, with the catalogs attached at each level. */
  async getUserContext(userId: string): Promise<ApiResponse<UserContextResponse>> {
    try {
      return await this.get<ApiResponse<UserContextResponse>>(`/users/directory/${userId}/context`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load user context'
      };
    }
  }

  // User management endpoints
  async getUserProfile(userId: string): Promise<ApiResponse<User>> {
    try {
      const response = await this.get<ApiResponse<User>>(`/users/${userId}/profile`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get user profile'
      };
    }
  }

  /**
   * One page of supervisors, AMs or physicians, searched on the server. Back
   * pickers with this (through AsyncSearchSelect / useServerList), never with
   * getUsers: there are more users than one page holds.
   */
  async searchUsersPage(params: {
    role: 'supervisor' | 'distributor' | 'physician';
    search?: string;
    page?: number;
    pageSize?: number;
    distributorId?: string;
    supervisorId?: string;
    status?: string;
    signal?: AbortSignal;
  }): Promise<ServerPage<User>> {
    const endpoint = params.role === 'supervisor' ? '/users/supervisors' : params.role === 'distributor' ? '/users/distributors' : '/users/physicians';
    const page = params.page ?? 1;
    const pageSize = Math.min(params.pageSize ?? 25, 100);
    const query = new URLSearchParams({ page: String(page), limit: String(pageSize) });
    if (params.search?.trim()) query.set('search', params.search.trim());
    if (params.distributorId) query.set('distributorId', params.distributorId);
    if (params.supervisorId) query.set('supervisorId', params.supervisorId);
    if (params.status) query.set('status', params.status);
    const response = await this.get<any>(`${endpoint}?${query}`, { signal: params.signal });
    const body = (response as any)?.data ?? response;
    const items: User[] = Array.isArray(body) ? body : body?.data ?? body?.users ?? [];
    const pagination = normalizePagination(body?.pagination, { page, pageSize, itemCount: items.length });
    return { items, total: pagination.total, hasMore: pagination.hasMore };
  }

  /**
   * Every matching user, walking all pages. For exports and whole-population
   * filters only; a picker should search instead (searchUsersPage).
   */
  async getAllUsers(params: { role: 'supervisor' | 'distributor' | 'physician'; search?: string; distributorId?: string; supervisorId?: string; status?: string }): Promise<User[]> {
    const all: User[] = [];
    for (let page = 1; page <= 200; page++) {
      const next = await this.searchUsersPage({ ...params, page, pageSize: 100 });
      all.push(...next.items);
      if (!next.hasMore || next.items.length === 0) break;
    }
    return all;
  }

  /**
   * @deprecated Loads ONE page (default 500, the server's cap) and drops the
   * paging info, so anything past it silently disappears; there are already
   * more AMs than that. Use searchUsersPage for pickers and getAllUsers when
   * the whole population is truly needed. `truncated` reports a cut list.
   */
  async getUsers(params: {
    role: 'supervisor' | 'distributor' | 'physician';
    distributorId?: string;
    supervisorId?: string;
    search?: string;
    limit?: number;
  }): Promise<ApiResponse<{ users: User[]; truncated?: boolean }>> {
    try {
      const endpoint = params.role === 'supervisor' ? '/users/supervisors' : params.role === 'distributor' ? '/users/distributors' : '/users/physicians';
      const queryParams = new URLSearchParams();

      if (params.distributorId) {
        queryParams.append('distributorId', params.distributorId);
      }
      if (params.supervisorId) {
        queryParams.append('supervisorId', params.supervisorId);
      }
      // Forward the search term so the backend filters server-side (the dropdown
      // no longer relies on loading every recipient and filtering in-browser).
      if (params.search && params.search.trim()) {
        queryParams.append('search', params.search.trim());
      }
      // Request more users for dropdown use
      queryParams.append('limit', (params.limit ?? 500).toString());
      
      const url = `${endpoint}?${queryParams.toString()}`;
      const response = await this.get<ApiResponse<any>>(url);
      
      // Backend returns paginated response { data: [...], pagination: {...} }
      if (response.success && response.data) {
        // Handle both old format (direct array) and new format (paginated object)
        let users: User[];
        if (Array.isArray(response.data)) {
          users = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          users = response.data.data;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          users = response.data.users;
        } else {
          users = [];
        }
        const truncated = Boolean(!Array.isArray(response.data) && response.data.pagination?.hasMore);
        if (truncated && import.meta.env.DEV) {
          console.warn(`getUsers(${params.role}) returned a truncated list; use searchUsersPage or getAllUsers.`);
        }
        return {
          success: true,
          data: {
            users,
            truncated
          }
        };
      }
      
      return {
        success: false,
        error: 'No data returned'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get users'
      };
    }
  }

  // Payment Instructions endpoints
  async updatePaymentInstructions(paymentInstructions: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>('/account-managers/payment-instructions', {
        paymentInstructions
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update payment instructions'
      };
    }
  }

  async getPaymentInstructions(amId: string): Promise<ApiResponse<{
    paymentInstructions: string;
    amInfo: {
      id: string;
      email: string;
      name: string;
      companyName: string;
    };
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        paymentInstructions: string;
        amInfo: {
          id: string;
          email: string;
          name: string;
          companyName: string;
        };
      }>>(`/admin/account-managers/${amId}/payment-instructions`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get payment instructions'
      };
    }
  }

  async getAllPaymentInstructions(): Promise<ApiResponse<{
    accountManagers: Array<{
      id: string;
      email: string;
      name: string;
      companyName: string;
      paymentInstructions: string;
    }>;
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        accountManagers: Array<{
          id: string;
          email: string;
          name: string;
          companyName: string;
          paymentInstructions: string;
        }>;
      }>>('/admin/account-managers/payment-instructions');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get all payment instructions'
      };
    }
  }

  async updatePaymentInstructionsAsAdmin(amId: string, paymentInstructions: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/admin/account-managers/${amId}/payment-instructions`, {
        paymentInstructions
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update payment instructions'
      };
    }
  }

  // ---- Integration (partner) API credentials ----
  // A partner IS a supervisor account; these only manage the machine credential
  // attached to it. The partner routes answer `{ data }` / `{ error: {code,message} }`
  // rather than the `{ success }` envelope, so normalize here.
  private partnerError(error: any, fallback: string) {
    return { success: false as const, error: error.response?.data?.error?.message || error.response?.data?.error || fallback };
  }

  async getApiPartners(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.client.get('/admin/api-partners');
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to load integrations');
    }
  }

  /** Turn API access on for a supervisor and issue its key (returned once). */
  async enableApiPartner(supervisorUserId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.put(`/admin/api-partners/${supervisorUserId}`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to enable API access');
    }
  }

  async updateApiPartner(partnerId: string, updates: {
    apiEnabled?: boolean; status?: 'active' | 'suspended'; onboardingDistributorId?: string;
    // Payment terms, in the same shape as a clinic's AR settings.
    billing?: { arEnabled: boolean; paymentTerms: 'immediate' | 'net7' | 'net15' | 'net30'; creditLimit: number | null };
    // The partner's signed doctor-network contract; null withdraws it.
    networkAgreement?: { telemedNetworkId: string; version?: string } | null;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.patch(`/admin/api-partners/${partnerId}`, updates);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to update the integration');
    }
  }

  /** Every onboarding a partner started, with progress and the physician behind it. */
  async getApiPartnerOnboardings(partnerId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.client.get(`/admin/api-partners/${partnerId}/onboardings`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to load onboardings');
    }
  }

  /** Staff confirm a physician's direct contact details and invite them to accept the connection. */
  async reviewApiPartnerPhysicianContact(partnerId: string, physicianId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.post(`/admin/api-partners/${partnerId}/physicians`, { physicianId, directContactReviewed: true });
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to review the physician contact');
    }
  }

  async rotateApiPartnerKey(partnerId: string): Promise<ApiResponse<{
    apiKey: string; previousExpiresAt?: string; graceHours?: number;
  }>> {
    try {
      const response = await this.client.post(`/admin/api-partners/${partnerId}/rotate-key`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to rotate the key');
    }
  }

  // ---- A partner managing their own integration tokens ----
  // `apiKey` comes back exactly once, on create and rotate. There is no endpoint
  // that returns it again, by design.

  async listMyApiTokens(): Promise<ApiResponse<{
    tokens: SupervisorApiTokenView[]; hasLegacyCredential: boolean; graceHours: number;
  }>> {
    try {
      const response = await this.client.get('/supervisor/api-tokens');
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to load your API tokens');
    }
  }

  async createMyApiToken(label?: string): Promise<ApiResponse<{
    apiKey: string; token: SupervisorApiTokenView;
  }>> {
    try {
      const response = await this.client.post('/supervisor/api-tokens', label ? { label } : {});
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to create the token');
    }
  }

  async rotateMyApiToken(tokenId: string): Promise<ApiResponse<{
    apiKey: string; token: SupervisorApiTokenView; previousExpiresAt: string; graceHours: number;
  }>> {
    try {
      const response = await this.client.post(`/supervisor/api-tokens/${tokenId}/rotate`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to rotate the token');
    }
  }

  /** Adopts a pre-token-list credential into the list, with the same grace. */
  async rotateMyLegacyApiKey(): Promise<ApiResponse<{
    apiKey: string; token: SupervisorApiTokenView; previousExpiresAt: string; graceHours: number;
  }>> {
    try {
      const response = await this.client.post('/supervisor/api-tokens/rotate-legacy');
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to rotate your original key');
    }
  }

  async revokeMyApiToken(tokenId: string): Promise<ApiResponse<{ token: SupervisorApiTokenView }>> {
    try {
      const response = await this.client.delete(`/supervisor/api-tokens/${tokenId}`);
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to revoke the token');
    }
  }

  async revokeMyLegacyApiKey(): Promise<ApiResponse<{ revoked: boolean }>> {
    try {
      const response = await this.client.delete('/supervisor/api-tokens/legacy');
      return { success: true, data: response.data.data };
    } catch (error: any) {
      return this.partnerError(error, 'Failed to revoke your original key');
    }
  }

  // Patient management endpoints
  async getPatients(options: {
    query?: string;
    limit?: number;
    skip?: number;
    sortBy?: 'name' | 'dateOfBirth' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (options.query) queryParams.append('query', options.query);
      if (options.limit) queryParams.append('limit', options.limit.toString());
      if (options.skip) queryParams.append('skip', options.skip.toString());
      if (options.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);
      
      const url = `/patients${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.get<ApiResponse<any[]>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get patients'
      };
    }
  }

  /**
   * One page of this clinic's active patients, searched on the server (name,
   * phone, email). Back patient pickers and rosters with this through
   * AsyncSearchSelect / useServerList; one getPatients() page is only the
   * newest 50.
   */
  async searchPatientsPage(params: {
    search?: string;
    page?: number;
    pageSize?: number;
    sortBy?: 'name' | 'dateOfBirth' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
    signal?: AbortSignal;
  } = {}): Promise<ServerPage<any>> {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 25;
    const query = new URLSearchParams({ limit: String(pageSize), skip: String((page - 1) * pageSize) });
    if (params.search?.trim()) query.set('query', params.search.trim());
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);
    const response = await this.get<any>(`/patients?${query}`, { signal: params.signal });
    if (response?.success === false) throw new Error(response.error || 'Failed to load patients');
    const items: any[] = Array.isArray(response?.data) ? response.data : [];
    const pagination = normalizePagination(response?.pagination, { page, pageSize, itemCount: items.length });
    return { items, total: pagination.total, hasMore: pagination.hasMore };
  }

  async getPatient(patientId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/patients/${patientId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get patient'
      };
    }
  }

  async createPatient(patientData: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/patients', patientData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create patient'
      };
    }
  }

  async bulkImportPatients(patients: any[]): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/patients/bulk-import', { patients });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to bulk import patients'
      };
    }
  }

  /**
   * Open this patient's own portal as them. Returns a single-use link rather
   * than a session, because the clinic's storefront may be on its own domain.
   */
  async impersonatePatient(patientId: string, options: { reason?: string; destination?: string } = {}): Promise<ApiResponse<{ url: string; sessionId: string; expiresInSeconds: number }>> {
    try {
      const response = await this.client.post(`/patients/${patientId}/impersonate`, options);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Could not open the patient portal' };
    }
  }

  async updatePatient(patientId: string, patientData: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/patients/${patientId}`, patientData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update patient'
      };
    }
  }

  async markPatientInactive(patientId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/patients/${patientId}/inactive`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark patient as inactive'
      };
    }
  }

  async searchPatients(query: string, options: {
    limit?: number;
    skip?: number;
  } = {}): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('q', query);
      if (options.limit) queryParams.append('limit', options.limit.toString());
      if (options.skip) queryParams.append('skip', options.skip.toString());
      
      const url = `/patients/search?${queryParams.toString()}`;
      const response = await this.get<ApiResponse<any[]>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to search patients'
      };
    }
  }

  async getPatientStats(): Promise<ApiResponse<{
    totalPatients: number;
    newPatientsThisMonth: number;
    activePatients: number;
    totalHealthDataEntries: number;
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        totalPatients: number;
        newPatientsThisMonth: number;
        activePatients: number;
        totalHealthDataEntries: number;
      }>>('/patients/stats');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get patient statistics'
      };
    }
  }

  async addPatientHealthData(patientId: string, healthData: {
    question: string;
    response: string;
    metric: string;
    timestamp?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/patients/${patientId}/health-data`, healthData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to add health data'
      };
    }
  }

  async getPatientHealthData(patientId: string, options: {
    metric?: string;
    limit?: number;
    skip?: number;
    startDate?: number;
    endDate?: number;
  } = {}): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (options.metric) queryParams.append('metric', options.metric);
      if (options.limit) queryParams.append('limit', options.limit.toString());
      if (options.skip) queryParams.append('skip', options.skip.toString());
      if (options.startDate) queryParams.append('startDate', options.startDate.toString());
      if (options.endDate) queryParams.append('endDate', options.endDate.toString());
      
      const url = `/patients/${patientId}/health-data${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.get<ApiResponse<any[]>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get health data'
      };
    }
  }

  async getPatientHealthMetrics(patientId: string): Promise<ApiResponse<string[]>> {
    try {
      const response = await this.get<ApiResponse<string[]>>(`/patients/${patientId}/health-metrics`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get health metrics'
      };
    }
  }

  // Order Methods
  async getOrders(options: {
    status?: string;
    patientId?: string;
    /** Order number or patient name, matched on the server over every order. */
    search?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    skip?: number;
    sortBy?: string;
    sortOrder?: string;
  } = {}): Promise<ApiResponse<{ orders: any[], pagination: any }>> {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
      
      const queryString = queryParams.toString();
      const url = `/orders${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.get<ApiResponse<{ orders: any[], pagination: any }>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get orders'
      };
    }
  }

  async getShippingRate(isBulk: boolean = false): Promise<ApiResponse<{ shippingRate: number; source: string; isBulk: boolean }>> {
    try {
      const query = isBulk ? '?isBulk=true' : '';
      const response = await this.get<ApiResponse<{ shippingRate: number; source: string; isBulk: boolean }>>(`/shipping-rate${query}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get shipping rate'
      };
    }
  }

  // ============================================================
  // ADMIN SHIPPING SETTINGS
  // ============================================================

  async getAdminShippingSettings(): Promise<ApiResponse<{
    defaultShippingRate: number;
    bulkOrderShippingRate: number;
  }>> {
    try {
      const response = await this.get<ApiResponse<{
        defaultShippingRate: number;
        bulkOrderShippingRate: number;
      }>>('/admin/settings/shipping');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get shipping settings'
      };
    }
  }

  async updateAdminShippingSettings(settings: {
    defaultShippingRate?: number;
    bulkOrderShippingRate?: number;
  }): Promise<ApiResponse<{
    defaultShippingRate: number;
    bulkOrderShippingRate: number;
  }>> {
    try {
      const response = await this.put<ApiResponse<{
        defaultShippingRate: number;
        bulkOrderShippingRate: number;
      }>>('/admin/settings/shipping', settings);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update shipping settings'
      };
    }
  }

  async getAMShippingRates(amId: string): Promise<ApiResponse<{
    amId: string;
    amName: string;
    companyName: string;
    shippingRate?: number;
    bulkShippingRate?: number;
    effectiveShippingRate: number;
    effectiveBulkShippingRate: number;
    globalDefaults: {
      defaultShippingRate: number;
      bulkOrderShippingRate: number;
    };
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/admin/account-managers/${amId}/shipping-rates`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get AM shipping rates'
      };
    }
  }

  async updateAMShippingRates(amId: string, rates: {
    shippingRate?: number | null;
    bulkShippingRate?: number | null;
  }): Promise<ApiResponse<{
    amId: string;
    shippingRate?: number;
    bulkShippingRate?: number;
    effectiveShippingRate: number;
    effectiveBulkShippingRate: number;
  }>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/admin/account-managers/${amId}/shipping-rates`, rates);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update AM shipping rates'
      };
    }
  }

  async getAllAMShippingRates(): Promise<ApiResponse<{
    accountManagers: Array<{
      id: string;
      email: string;
      name: string;
      companyName: string;
      shippingRate?: number;
      bulkShippingRate?: number;
      effectiveShippingRate: number;
      effectiveBulkShippingRate: number;
      hasShippingOverride: boolean;
      hasBulkShippingOverride: boolean;
    }>;
    globalDefaults: {
      defaultShippingRate: number;
      bulkOrderShippingRate: number;
    };
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>('/admin/account-managers/shipping-rates');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get all AM shipping rates'
      };
    }
  }

  async getAllPhysicianShippingRates(): Promise<ApiResponse<{
    physicians: Array<{
      id: string;
      email: string;
      name: string;
      clinicName: string;
      accountManager: {
        id: string;
        name: string;
        companyName: string;
      } | null;
      shippingRate?: number;
      bulkShippingRate?: number;
      effectiveShippingRate: number;
      effectiveBulkShippingRate: number;
      shippingSource: string;
      bulkShippingSource: string;
      hasShippingOverride: boolean;
      hasBulkShippingOverride: boolean;
    }>;
    globalDefaults: {
      defaultShippingRate: number;
      bulkOrderShippingRate: number;
    };
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>('/admin/physicians/shipping-rates');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician shipping rates'
      };
    }
  }

  async getPhysicianShippingRates(physicianId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/admin/physicians/${physicianId}/shipping-rates`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician shipping rates'
      };
    }
  }

  async updatePhysicianShippingRates(physicianId: string, rates: {
    shippingRate?: number | null;
    bulkShippingRate?: number | null;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/admin/physicians/${physicianId}/shipping-rates`, rates);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update physician shipping rates'
      };
    }
  }

  async getShippingPreview(physicianId: string, isBulk: boolean = false): Promise<ApiResponse<{
    physicianId: string;
    physicianName: string;
    orderType: 'bulk' | 'patient';
    shippingRate: number;
    source: 'physician_override' | 'am_override' | 'system_default';
    physicianOverride?: number;
    accountManager: {
      id: string;
      name: string;
      companyName: string;
      hasOverride: boolean;
    } | null;
    globalDefault: number;
  }>> {
    try {
      const query = isBulk ? '?isBulk=true' : '';
      const response = await this.get<ApiResponse<any>>(`/admin/shipping-preview/${physicianId}${query}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get shipping preview'
      };
    }
  }

  async createOrder(orderData: {
    patientId: string;
    paymentMethod?: 'patient_pay' | 'physician_billed';
    shippingDestination?: 'patient' | 'clinic' | 'prescriber';
    items: {
      productId: string;
      quantity: number;
      physicianMarkup?: number;
      selectedVariants?: Record<string, string>;
    }[];
    // Pharmacy accounts: the reviewed transfer record this order fills.
    prescriptionTransferId?: string;
    // Pharmacy accounts only: the outside doctor's script this order fills.
    prescriptionTransfer?: {
      source: 'pdf' | 'manual';
      prescriber: {
        firstName: string;
        lastName: string;
        npiNumber: string;
        licenseState?: string;
        phone?: string;
      };
      documentFileName?: string;
      documentS3Key?: string;
      documentUrl?: string;
      notes?: string;
    };
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/orders', orderData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create order'
      };
    }
  }

  async getOrder(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/orders/${orderId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get order'
      };
    }
  }

  async submitOrder(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/orders/${orderId}/submit`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit order'
      };
    }
  }

  async markOrderAsPending(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/orders/${orderId}/pending`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark order as pending'
      };
    }
  }

  async markOrderAsFailed(orderId: string, errorMessage?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/orders/${orderId}/failed`, {
        errorMessage: errorMessage || 'Order failed'
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark order as failed'
      };
    }
  }

  // Write script - finalize a draft order (physician-billed or patient-pay) and create/send invoice
  async writeScript(orderId: string): Promise<ApiResponse<{ order: any; invoice: any; paymentLinkSent?: boolean; paymentLinkError?: string | null; autoCharged?: boolean; autoChargeError?: string }>> {
    try {
      const response = await this.post<ApiResponse<{ order: any; invoice: any; paymentLinkSent?: boolean; paymentLinkError?: string | null; autoCharged?: boolean; autoChargeError?: string }>>(`/orders/${orderId}/write-script`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to write script'
      };
    }
  }

  async getProducts(params?: { 
    page?: number; 
    limit?: number; 
    search?: string;
    category?: string;
    format?: string;
    excludeIds?: string;
    /** 'medication' | 'supply' | 'lab', comma-separated for several. */
    productType?: string;
    includePricing?: boolean;
    signal?: AbortSignal;
  }): Promise<ApiResponse<{ products: any[], pagination: any }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.category) queryParams.append('category', params.category);
      if (params?.format) queryParams.append('format', params.format);
      if (params?.excludeIds) queryParams.append('excludeIds', params.excludeIds);
      if (params?.productType) queryParams.append('productType', params.productType);
      if (params?.includePricing) queryParams.append('includePricing', 'true');
      
      const url = queryParams.toString() ? `/products?${queryParams.toString()}` : '/products';
      const response = await this.get<ApiResponse<{ products: any[], pagination: any }>>(url, { signal: params?.signal });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch products'
      };
    }
  }

  /**
   * Every active product, paginated.
   *
   * The products endpoint caps `limit` at 100 server-side and says nothing
   * about it, so `getProducts({ limit: 500 })` silently returns 100 and the
   * caller believes it has the lot. Anywhere that genuinely needs the whole
   * catalog should use this instead.
   */
  async getAllProducts(pageSize = 100, filters: { productType?: string; category?: string } = {}): Promise<ApiResponse<{ products: any[] }>> {
    try {
      const all: any[] = [];
      let page = 1;
      let total = Infinity;
      // Bounded so a bad `total` can't spin forever.
      while (all.length < total && page <= 100) {
        const res = await this.getProducts({ ...filters, page, limit: pageSize });
        if (!res.success) {
          return { success: false, error: res.error || 'Failed to load products' };
        }
        const batch = (res as any).data?.products || [];
        all.push(...batch);
        total = (res as any).data?.pagination?.total ?? all.length;
        if (batch.length === 0) break;
        page += 1;
      }
      return { success: true, data: { products: all } };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load products' };
    }
  }

  async getProductsByIds(productIds: string[]): Promise<ApiResponse<{ products: any[], requested: number, found: number }>> {
    try {
      const response = await this.post<ApiResponse<{ products: any[], requested: number, found: number }>>(
        '/products/by-ids',
        { productIds }
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch products by IDs'
      };
    }
  }

  async getProductsByIdsWithCost(productIds: string[]): Promise<ApiResponse<{ products: any[], requested: number, found: number }>> {
    try {
      const response = await this.post<ApiResponse<{ products: any[], requested: number, found: number }>>(
        '/products/by-ids-with-cost',
        { productIds }
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch products with cost'
      };
    }
  }

  async cancelOrder(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/orders/${orderId}/cancel`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to cancel order'
      };
    }
  }

  async getOrderPaymentStatus(orderId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/orders/${orderId}/payment-status`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch payment status'
      };
    }
  }

  // ==========================================================================
  // Payment Methods Management (Card on File)
  // ==========================================================================

  async createSetupIntent(): Promise<ApiResponse<{ clientSecret: string; publishableKey: string; paymentIntentId: string }>> {
    try {
      const response = await this.post<ApiResponse<{ clientSecret: string; publishableKey: string; paymentIntentId: string }>>('/payments/setup-intent', {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create setup intent'
      };
    }
  }

  async createVaultSession(): Promise<ApiResponse<{ pmSessionId: string; pmClientSecret: string; publishableKey: string; profileId: string }>> {
    try {
      const response = await this.post<ApiResponse<{ pmSessionId: string; pmClientSecret: string; publishableKey: string; profileId: string }>>('/payments/vault-session', {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create vault session'
      };
    }
  }

  async getPaymentMethods(): Promise<ApiResponse<{ paymentMethods: any[]; defaultPaymentMethodId: string | null; resaveRequired?: { paymentMethodId: string; flaggedAt?: string } | null }>> {
    try {
      const response = await this.get<ApiResponse<{ paymentMethods: any[]; defaultPaymentMethodId: string | null; resaveRequired?: { paymentMethodId: string; flaggedAt?: string } | null }>>('/payments/methods');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get payment methods'
      };
    }
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.delete<ApiResponse<{ message: string }>>(`/payments/methods/${paymentMethodId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete payment method'
      };
    }
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.post<ApiResponse<{ message: string }>>(`/payments/methods/${paymentMethodId}/default`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to set default payment method'
      };
    }
  }

  async getPhysicianPaymentMethods(physicianId: string): Promise<ApiResponse<{ paymentMethods: any[]; defaultPaymentMethodId: string | null }>> {
    try {
      const response = await this.get<ApiResponse<{ paymentMethods: any[]; defaultPaymentMethodId: string | null }>>(`/payments/physicians/${physicianId}/methods`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician payment methods'
      };
    }
  }

  async chargeInvoice(invoiceId: string, paymentMethodId?: string): Promise<ApiResponse<{ paymentIntentId: string; status: string }>> {
    try {
      const response = await this.post<ApiResponse<{ paymentIntentId: string; status: string }>>(`/invoices/${invoiceId}/charge`, { 
        paymentMethodId 
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to charge invoice'
      };
    }
  }

  async payInvoice(invoiceId: string, paymentMethodId?: string): Promise<ApiResponse<{ paymentIntentId: string; status: string }>> {
    try {
      const response = await this.post<ApiResponse<{ paymentIntentId: string; status: string }>>(`/invoices/${invoiceId}/pay`, {
        paymentMethodId
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to pay invoice'
      };
    }
  }

  // Mark invoice as paid (admin only) - for physician-billed orders
  async markInvoiceAsPaid(
    invoiceId: string,
    paymentMethod: 'card' | 'ach' | 'check' | 'cash' | 'other',
    transactionId?: string,
    notes?: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/invoices/${invoiceId}/mark-paid`, {
        paymentMethod,
        transactionId,
        notes
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark invoice as paid'
      };
    }
  }

  async getOrderStats(): Promise<ApiResponse<{
    totalOrders: number;
    pendingOrders: number;
    submittedOrders: number;
    deliveredOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>('/orders/stats');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get order stats'
      };
    }
  }

  // Analytics Methods
  async getAnalytics(): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>('/analytics/me');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get analytics'
      };
    }
  }

  async getPhysicianAnalytics(
    physicianId?: string,
    dateRange?: { startDate?: Date; endDate?: Date },
    intervals?: { revenueInterval?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const url = physicianId ? `/analytics/physician/${physicianId}` : '/analytics/physician';
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (intervals?.revenueInterval) params.append('revenueInterval', intervals.revenueInterval);

      const finalUrl = params.toString() ? `${url}?${params}` : url;
      const response = await this.get<ApiResponse<any>>(finalUrl);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician analytics'
      };
    }
  }

  async getDistributorAnalytics(
    distributorId?: string,
    dateRange?: { startDate?: Date; endDate?: Date },
    intervals?: { revenueInterval?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const url = distributorId ? `/analytics/distributor/${distributorId}` : '/analytics/distributor';
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (intervals?.revenueInterval) params.append('revenueInterval', intervals.revenueInterval);

      const finalUrl = params.toString() ? `${url}?${params}` : url;
      const response = await this.get<ApiResponse<any>>(finalUrl);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get distributor analytics'
      };
    }
  }

  async getSupervisorAnalytics(
    dateRange?: { startDate?: Date; endDate?: Date },
    intervals?: { revenueInterval?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (intervals?.revenueInterval) params.append('revenueInterval', intervals.revenueInterval);

      const url = params.toString() ? `/analytics/supervisor?${params}` : '/analytics/supervisor';
      const response = await this.get<ApiResponse<any>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get supervisor analytics'
      };
    }
  }

  // Piecewise supervisor analytics — three smaller endpoints that the
  // supervisor dashboard queries independently so the page can render in
  // stages. The combined getSupervisorAnalytics() above still works for
  // callers that want everything at once.
  async getSupervisorAnalyticsHeader(
    dateRange?: { startDate?: Date; endDate?: Date }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      const url = params.toString() ? `/analytics/supervisor/header?${params}` : '/analytics/supervisor/header';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get supervisor analytics header'
      };
    }
  }

  async getSupervisorAnalyticsTrends(
    dateRange?: { startDate?: Date; endDate?: Date },
    intervals?: { revenueInterval?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (intervals?.revenueInterval) params.append('revenueInterval', intervals.revenueInterval);
      const url = params.toString() ? `/analytics/supervisor/trends?${params}` : '/analytics/supervisor/trends';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get supervisor analytics trends'
      };
    }
  }

  async getSupervisorAnalyticsRankings(
    dateRange?: { startDate?: Date; endDate?: Date }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      const url = params.toString() ? `/analytics/supervisor/rankings?${params}` : '/analytics/supervisor/rankings';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get supervisor analytics rankings'
      };
    }
  }

  async getSystemAnalytics(
    dateRange?: { startDate?: Date; endDate?: Date },
    intervals?: { userGrowthInterval?: string; revenueInterval?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (intervals?.userGrowthInterval) params.append('userGrowthInterval', intervals.userGrowthInterval);
      if (intervals?.revenueInterval) params.append('revenueInterval', intervals.revenueInterval);

      const url = params.toString() ? `/analytics/system?${params}` : '/analytics/system';
      const response = await this.get<ApiResponse<any>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get system analytics'
      };
    }
  }

  // Piecewise admin analytics — three smaller endpoints that the dashboard
  // queries independently so the page can render in stages. The combined
  // getSystemAnalytics() above still works for callers that want everything
  // at once (e.g. the Analytics page).
  async getAdminAnalyticsHeader(
    dateRange?: { startDate?: Date; endDate?: Date }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      const url = params.toString() ? `/analytics/system/header?${params}` : '/analytics/system/header';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get system analytics header'
      };
    }
  }

  async getAdminAnalyticsTrends(
    dateRange?: { startDate?: Date; endDate?: Date },
    intervals?: { userGrowthInterval?: string; revenueInterval?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (intervals?.userGrowthInterval) params.append('userGrowthInterval', intervals.userGrowthInterval);
      if (intervals?.revenueInterval) params.append('revenueInterval', intervals.revenueInterval);
      const url = params.toString() ? `/analytics/system/trends?${params}` : '/analytics/system/trends';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get system analytics trends'
      };
    }
  }

  async getAdminAnalyticsRankings(
    dateRange?: { startDate?: Date; endDate?: Date }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      const url = params.toString() ? `/analytics/system/rankings?${params}` : '/analytics/system/rankings';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get system analytics rankings'
      };
    }
  }

  // Internal sales-rep leaderboard (salesRepEnabled distributors ranked vs.
  // each other and the median AM).
  async getSalesRepLeaderboard(
    dateRange?: { startDate?: Date; endDate?: Date }
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      const url = params.toString() ? `/analytics/system/sales-reps?${params}` : '/analytics/system/sales-reps';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get sales rep leaderboard'
      };
    }
  }

  // Product best-sellers / high-velocity. Pass distributorId to scope to a
  // single rep's book of business; omit for system-wide.
  async getProductVelocity(
    dateRange?: { startDate?: Date; endDate?: Date },
    distributorId?: string
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (distributorId) params.append('distributorId', distributorId);
      const url = params.toString() ? `/analytics/system/product-velocity?${params}` : '/analytics/system/product-velocity';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get product velocity'
      };
    }
  }

  // Account Health (admin). Slipping accounts + AM rollup, month-over-month
  // revenue retention, signup activation, and volume-rewards reach.
  async getAccountHealthAccounts(params: {
    mode: 'rolling' | 'calendar';
    month?: string;
    threshold?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const qs = new URLSearchParams({ mode: params.mode });
      if (params.mode === 'calendar' && params.month) qs.append('month', params.month);
      if (params.threshold !== undefined) qs.append('threshold', String(params.threshold));
      return await this.get<ApiResponse<any>>(`/analytics/system/account-health/accounts?${qs}`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get account slippage'
      };
    }
  }

  async getAccountHealthRetention(months = 6): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/analytics/system/account-health/retention?months=${months}`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get revenue retention'
      };
    }
  }

  async getAccountHealthActivation(months = 6): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/analytics/system/account-health/activation?months=${months}`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get signup activation'
      };
    }
  }

  async getAccountHealthRewards(weeks = 8): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/analytics/system/account-health/rewards?weeks=${weeks}`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get rewards engagement'
      };
    }
  }

  // Piecewise distributor (AM) analytics — three smaller endpoints that the
  // AM dashboard queries independently so the page can render in stages.
  // The combined getDistributorAnalytics() above still works for callers
  // that want the unified shape.
  async getDistributorAnalyticsHeader(
    distributorId?: string,
    dateRange?: { startDate?: Date; endDate?: Date }
  ): Promise<ApiResponse<any>> {
    try {
      const id = distributorId || 'me';
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      const base = `/analytics/distributor/${id}/header`;
      const url = params.toString() ? `${base}?${params}` : base;
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get distributor analytics header'
      };
    }
  }

  async getDistributorAnalyticsTrends(
    distributorId?: string,
    dateRange?: { startDate?: Date; endDate?: Date },
    intervals?: { revenueInterval?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const id = distributorId || 'me';
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      if (intervals?.revenueInterval) params.append('revenueInterval', intervals.revenueInterval);
      const base = `/analytics/distributor/${id}/trends`;
      const url = params.toString() ? `${base}?${params}` : base;
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get distributor analytics trends'
      };
    }
  }

  async getDistributorAnalyticsRankings(
    distributorId?: string,
    dateRange?: { startDate?: Date; endDate?: Date }
  ): Promise<ApiResponse<any>> {
    try {
      const id = distributorId || 'me';
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      const base = `/analytics/distributor/${id}/rankings`;
      const url = params.toString() ? `${base}?${params}` : base;
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get distributor analytics rankings'
      };
    }
  }

  async getDailyOrderVolume(days: number = 30): Promise<ApiResponse<{
    days: { date: string; total: number; byStatus: Record<string, number> }[];
  }>> {
    try {
      return await this.get<ApiResponse<any>>(`/analytics/system/daily-orders?days=${days}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to get daily orders' };
    }
  }

  async getAdminAmRankings(options?: {
    startDate?: Date;
    endDate?: Date;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    rankings: Array<{
      distributorId: string;
      distributorName: string;
      physicianCount: number;
      orderCount: number;
      revenue: number;
      averageOrderValue: number;
      averageOrderInterval: number | null;
      firstOrderDate: string | null;
      lastOrderDate: string | null;
    }>;
    pagination: { page: number; limit: number; totalCount: number; totalPages: number; hasMore: boolean };
  }>> {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate.toISOString());
      if (options?.endDate) params.append('endDate', options.endDate.toISOString());
      if (options?.page) params.append('page', String(options.page));
      if (options?.limit) params.append('limit', String(options.limit));
      const qs = params.toString();
      return await this.get<ApiResponse<any>>(`/analytics/system/rankings/account-managers${qs ? `?${qs}` : ''}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to get AM rankings' };
    }
  }

  async getAdminPhysicianRankings(options?: {
    startDate?: Date;
    endDate?: Date;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    rankings: Array<{
      physicianId: string;
      physicianName: string;
      clinicName: string | null;
      orderCount: number;
      revenue: number;
      averageOrderValue: number;
      averageOrderInterval: number | null;
      firstOrderDate: string | null;
      lastOrderDate: string | null;
    }>;
    pagination: { page: number; limit: number; totalCount: number; totalPages: number; hasMore: boolean };
  }>> {
    try {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate.toISOString());
      if (options?.endDate) params.append('endDate', options.endDate.toISOString());
      if (options?.page) params.append('page', String(options.page));
      if (options?.limit) params.append('limit', String(options.limit));
      const qs = params.toString();
      return await this.get<ApiResponse<any>>(`/analytics/system/rankings/physicians${qs ? `?${qs}` : ''}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to get physician rankings' };
    }
  }

  async getAccountManagerPerformance(
    amId: string,
    options: { interval?: 'day' | 'week' | 'month'; startDate?: Date; endDate?: Date } = {}
  ): Promise<ApiResponse<{
    am: { id: string; name: string; networkSize: number };
    interval: 'day' | 'week' | 'month';
    cohortSize: number;
    series: { bucket: string; revenue: number; amMargin: number; orderCount: number; activePhysicians: number; activePhysicianPct: number; avgCompositeOrderSize: number; reorderVelocity: number }[];
    cohort: { bucket: string; revenue: { p25: number; p50: number; p75: number }; amMargin: { p25: number; p50: number; p75: number }; orderCount: { p25: number; p50: number; p75: number }; activePhysicianPct: { p25: number; p50: number; p75: number }; avgCompositeOrderSize: { p25: number; p50: number; p75: number }; reorderVelocity: { p25: number; p50: number; p75: number } }[];
    summary: Record<string, { value: number; cohortP25: number; cohortP50: number; cohortP75: number; percentile: number }>;
  }>> {
    try {
      const params = new URLSearchParams();
      if (options.interval) params.set('interval', options.interval);
      if (options.startDate) params.set('startDate', options.startDate.toISOString());
      if (options.endDate) params.set('endDate', options.endDate.toISOString());
      const qs = params.toString();
      return await this.get<ApiResponse<any>>(
        `/analytics/account-managers/${encodeURIComponent(amId)}/performance${qs ? `?${qs}` : ''}`
      );
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to get AM performance' };
    }
  }

  async getAvgPatientOrderSize(
    interval: 'day' | 'week' | 'month' = 'day',
    days: number = 30
  ): Promise<ApiResponse<{
    interval: 'day' | 'week' | 'month';
    buckets: { bucket: string; avgOrderSize: number; compositeOrderCount: number; totalRevenue: number }[];
  }>> {
    try {
      return await this.get<ApiResponse<any>>(`/analytics/system/avg-patient-order-size?interval=${interval}&days=${days}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to get avg patient order size' };
    }
  }

  async getFulfillmentSnapshot(stuckDays: number = 5): Promise<ApiResponse<{
    statusCounts: Record<string, number>;
    aging: Record<string, { count: number; avgDaysInStatus: number; oldestDays: number }>;
    stuck: { orderId: string; orderNumber: string; status: string; physicianName: string; daysInStatus: number; lastUpdatedAt: string }[];
  }>> {
    try {
      return await this.get<ApiResponse<any>>(`/analytics/system/fulfillment-status?stuckDays=${stuckDays}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to get fulfillment snapshot' };
    }
  }

  // ── Zendesk support integration ──────────────────────────────────────────

  async getZendeskIdentity(): Promise<ApiResponse<{
    token: string;
    widgetKey: string;
    subdomain: string;
    brandId: string | null;
  }>> {
    try {
      return await this.get<ApiResponse<any>>('/zendesk/identity');
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to get Zendesk identity' };
    }
  }

  async getOpenTickets(filters?: {
    status?: string;
    organization?: string;
    physicianId?: string;
  }): Promise<ApiResponse<{
    tickets: Array<{
      _id: string;
      zendeskTicketId: number;
      status: string;
      subject: string;
      requesterEmail: string;
      requesterPhysicianId?: string | null;
      organization?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    agentBaseUrl: string | null;
  }>> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.organization) params.append('organization', filters.organization);
      if (filters?.physicianId) params.append('physicianId', filters.physicianId);
      const url = params.toString() ? `/zendesk/tickets?${params}` : '/zendesk/tickets';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch tickets' };
    }
  }

  async getOpenTicketCountsByPhysician(physicianIds: string[]): Promise<ApiResponse<{
    counts: Record<string, number>;
  }>> {
    if (physicianIds.length === 0) {
      return { success: true, data: { counts: {} } };
    }
    try {
      const url = `/zendesk/tickets?countByPhysicianIds=${physicianIds.join(',')}`;
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch ticket counts' };
    }
  }

  async getPhysicianPhasedAnalytics(physicianId: string, dateRange?: { startDate?: Date; endDate?: Date }): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());

      const url = params.toString()
        ? `/analytics/physician/${physicianId}/phases?${params}`
        : `/analytics/physician/${physicianId}/phases`;

      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician phased analytics'
      };
    }
  }

  async getDistributorPhasedAnalytics(distributorId: string, dateRange?: { startDate?: Date; endDate?: Date }): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());

      const url = params.toString()
        ? `/analytics/distributor/${distributorId}/phases?${params}`
        : `/analytics/distributor/${distributorId}/phases`;

      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get distributor phased analytics'
      };
    }
  }

  async getSupervisorPhasedAnalytics(dateRange?: { startDate?: Date; endDate?: Date }): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());

      const url = params.toString() ? `/analytics/supervisor/phases?${params}` : '/analytics/supervisor/phases';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get supervisor phased analytics'
      };
    }
  }

  async getSystemPhasedAnalytics(dateRange?: { startDate?: Date; endDate?: Date }): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());

      const url = params.toString() ? `/analytics/system/phases?${params}` : '/analytics/system/phases';
      return await this.get<ApiResponse<any>>(url);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get system phased analytics'
      };
    }
  }

  async getRevenueBreakdown(
    revenueType: string,
    dateRange?: { startDate?: Date; endDate?: Date },
    page: number = 1,
    limit: number = 50,
    /** Searched and sorted on the server over every row, before paging. */
    options: { search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc' } = {}
  ): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams({ revenueType });
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate.toISOString());
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate.toISOString());
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (options.search?.trim()) params.append('search', options.search.trim());
      if (options.sortBy) params.append('sortBy', options.sortBy);
      if (options.sortOrder) params.append('sortOrder', options.sortOrder);

      const response = await this.get<ApiResponse<any>>(`/analytics/revenue-breakdown?${params}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get revenue breakdown'
      };
    }
  }

  async getPhysicianReorderRankings(options?: {
    page?: number;
    limit?: number;
    status?: 'all' | 'overdue' | 'due_soon' | 'recent' | 'new';
    search?: string;
    distributorId?: string; // For admin access
  }): Promise<ApiResponse<{
    rankings: Array<{
      physicianId: string;
      physicianName: string;
      clinicName: string;
      totalOrders: number;
      totalSpend: number;
      averageOrderValue: number;
      lastOrderDate: string | null;
      daysSinceLastOrder: number | null;
      averageOrderInterval: number | null;
      reorderScore: number;
      reorderStatus: 'overdue' | 'due_soon' | 'recent' | 'new';
    }>;
    pagination: {
      page: number;
      limit: number;
      totalCount: number;
      totalPages: number;
      hasMore: boolean;
    };
    summary: {
      overdue: number;
      dueSoon: number;
      recent: number;
      new: number;
    };
  }>> {
    try {
      const params = new URLSearchParams();
      if (options?.page) params.append('page', options.page.toString());
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.status) params.append('status', options.status);
      if (options?.search) params.append('search', options.search);
      if (options?.distributorId) params.append('distributorId', options.distributorId);

      const url = params.toString()
        ? `/analytics/distributor/reorder-rankings?${params}`
        : '/analytics/distributor/reorder-rankings';

      const response = await this.get<ApiResponse<any>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get reorder rankings'
      };
    }
  }

  // ── Pharmacy prescription transfers ───────────────────────────────────────
  // The inbox of outside doctors' scripts a pharmacy account handed us to fill.

  async uploadPrescriptionTransfer(file: File): Promise<ApiResponse<{ transfer: any; candidates: any[] }>> {
    try {
      const formData = new FormData();
      formData.append('filename', file.name);
      formData.append('fileData', file);

      const response = await this.client.post<ApiResponse<{ transfer: any; candidates: any[] }>>(
        '/prescription-transfers',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload the prescription'
      };
    }
  }

  async createManualPrescriptionTransfer(parsed: any): Promise<ApiResponse<{ transfer: any; candidates: any[] }>> {
    try {
      return await this.post<ApiResponse<{ transfer: any; candidates: any[] }>>(
        '/prescription-transfers/manual',
        { parsed }
      );
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to create the transfer' };
    }
  }

  /**
   * Newest transfers first. The route has no paging: it returns at most
   * `limit` (default 50, capped at 200) and no total.
   */
  async getPrescriptionTransfers(status?: string, limit?: number): Promise<ApiResponse<any[]>> {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (limit) params.set('limit', String(limit));
      const query = params.toString() ? `?${params}` : '';
      return await this.get<ApiResponse<any[]>>(`/prescription-transfers${query}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load transfers' };
    }
  }

  async getPrescriptionTransfer(id: string): Promise<ApiResponse<{ transfer: any; candidates: any[] }>> {
    try {
      return await this.get<ApiResponse<{ transfer: any; candidates: any[] }>>(`/prescription-transfers/${id}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load the transfer' };
    }
  }

  async updatePrescriptionTransfer(id: string, patch: {
    parsed?: any;
    notes?: string;
    matchedProductId?: string | null;
    patientId?: string | null;
    status?: 'ready' | 'needs_review';
  }): Promise<ApiResponse<{ transfer: any }>> {
    try {
      const response = await this.client.patch<ApiResponse<{ transfer: any }>>(
        `/prescription-transfers/${id}`,
        patch
      );
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to save your changes' };
    }
  }

  async rejectPrescriptionTransfer(id: string, reason?: string): Promise<ApiResponse<{ transfer: any }>> {
    try {
      return await this.post<ApiResponse<{ transfer: any }>>(`/prescription-transfers/${id}/reject`, { reason });
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to reject the transfer' };
    }
  }

  async getPrescriptionTransferDocument(id: string): Promise<ApiResponse<{ downloadUrl: string }>> {
    try {
      return await this.get<ApiResponse<{ downloadUrl: string }>>(`/prescription-transfers/${id}/document`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to open the document' };
    }
  }

  // Pharmacy accounts: upload the scanned outside prescription being transferred.
  // Returns the S3 key to attach to the order's prescriptionTransfer.
  async uploadRxTransferDocument(file: File): Promise<ApiResponse<{ fileName: string; fileKey: string; fileUrl: string }>> {
    try {
      const formData = new FormData();
      formData.append('filename', file.name);
      formData.append('fileData', file);

      const response = await this.client.post<ApiResponse<{ fileName: string; fileKey: string; fileUrl: string }>>(
        '/orders/rx-transfer-document',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload the prescription document'
      };
    }
  }

  // ID Proofing - Individual step uploads
  async uploadDriversLicense(physicianId: string, file: File): Promise<ApiResponse<{ message: string; filePath: string }>> {
    try {
      const formData = new FormData();
      formData.append('filename', file.name);
      formData.append('fileData', file);

      const response = await this.client.post<ApiResponse<{ message: string; filePath: string }>>(
        `/physicians/${physicianId}/id-proofing/drivers-license`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000,
        }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload drivers license'
      };
    }
  }

  async uploadSelfie(physicianId: string, photo: Blob): Promise<ApiResponse<{ message: string; filePath: string }>> {
    try {
      const formData = new FormData();
      formData.append('filename', 'selfie.jpg');
      formData.append('fileData', photo, 'selfie.jpg');

      const response = await this.client.post<ApiResponse<{ message: string; filePath: string }>>(
        `/physicians/${physicianId}/id-proofing/selfie`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000,
        }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload selfie'
      };
    }
  }

  async uploadSignature(physicianId: string, signature: Blob, agreementVersion?: string): Promise<ApiResponse<{ message: string; filePath: string }>> {
    try {
      // Convert Blob to base64 data URL
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(signature);
      });

      const requestData = {
        signatureData: base64Data,
        agreementAccepted: true,
        agreementVersion,
        timestamp: new Date().toISOString()
      };

      const response = await this.client.post<ApiResponse<{ message: string; filePath: string }>>(
        `/physicians/${physicianId}/id-proofing/signature`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 30000,
        }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload signature'
      };
    }
  }

  // Active telemed networks a physician can opt into (bolt-on gap coverage).
  async getAvailableTelemedNetworks(): Promise<ApiResponse<Array<{ _id: string; name: string; perVisitFeeCents: number }>>> {
    try {
      return await this.get<ApiResponse<any>>('/physicians/networks/available');
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load networks' };
    }
  }

  // The state licenses NPPES holds for the signed-in physician's own NPI, to
  // offer in the Settings editor. Read-only — importing is a separate save.
  async getMyNppesLicenses(): Promise<ApiResponse<{
    found: boolean;
    licenses: Array<{ state: string; licenseNumber?: string }>;
  }>> {
    try {
      return await this.get<ApiResponse<any>>('/users/me/npi-licenses');
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to look up NPPES licenses' };
    }
  }

  // Replace the signed-in physician's state licenses. Separate from the profile
  // save so an unrelated form can never clear them.
  async updateMyLicenses(
    licenses: Array<{ state: string; licenseNumber: string; expiresAt: string }>
  ): Promise<ApiResponse<{ licenses: PhysicianLicense[] }>> {
    try {
      return await this.put<ApiResponse<any>>('/users/me/licenses', { licenses });
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to update licenses' };
    }
  }

  // Request hybrid gap coverage: records the signed Contract + BAA and the
  // requested network/licenses; an admin approves before it goes live.
  async requestTelemedNetwork(physicianId: string, body: {
    telemedNetworkId: string;
    licenses?: Array<{ state: string; licenseNumber?: string; source?: 'nppes' | 'self' }>;
    signatureData: string;
    agreementVersion?: string;
    agreementAccepted: boolean;
  }): Promise<ApiResponse<{ message: string }>> {
    try {
      return await this.post<ApiResponse<any>>(`/physicians/${physicianId}/telemed-network/request`, body);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to submit network request' };
    }
  }

  // ID Proofing - Final submission (no files, just status change)
  async submitIdProofing(physicianId: string): Promise<ApiResponse<{ message: string; status: string; submittedAt: string }>> {
    try {
      const response = await this.client.post<ApiResponse<{ message: string; status: string; submittedAt: string }>>(
        `/physicians/${physicianId}/id-proofing/submit`,
        {}
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit ID proofing'
      };
    }
  }

  // ID Proofing - Get status
  async getIdProofingStatus(physicianId: string): Promise<ApiResponse<{ 
    idProofingStatus: string; 
    isVerified: boolean; 
    progress: any; 
    nextSteps: string[];
    submittedAt?: string;
    verifiedAt?: string;
  }>> {
    try {
      const response = await this.client.get<ApiResponse<{ 
        idProofingStatus: string; 
        isVerified: boolean; 
        progress: any; 
        nextSteps: string[];
        submittedAt?: string;
        verifiedAt?: string;
      }>>(
        `/physicians/${physicianId}/id-proofing/status`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get ID proofing status'
      };
    }
  }

  // Account Manager - Get physician documents and details
  async getPhysicianDocuments(physicianId: string): Promise<ApiResponse<{
    physician: {
      id: string;
      name: string;
      firstName?: string;
      lastName?: string;
      email: string;
      phone?: string;
      clinicName?: string;
      clinicAddress?: { street?: string; street2?: string; city?: string; state?: string; zipCode?: string };
      npiNumber: string;
      status: string;
      idProofingStatus: string;
      submittedAt?: string;
      verifiedAt?: string;
      verificationAttempts: number;
      agreementAccepted: boolean;
    };
    npiRegistryData?: {
      firstName: string;
      lastName: string;
      credential?: string;
      status?: string;
      enumerationDate?: string;
      organizationName?: string;
      addresses?: Array<{
        addressType: string;
        addressPurpose?: string;
        street1?: string;
        street2?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        telephoneNumber?: string;
      }>;
      taxonomies?: Array<{
        code?: string;
        description?: string;
        state?: string;
        license?: string;
        primary?: boolean;
      }>;
      fetchedAt?: string;
      notFound?: boolean;
    } | null;
    documents: {
      driversLicense?: {
        fileKey: string;
        uploadedAt: string;
        downloadUrl: string;
      };
      selfie?: {
        fileKey: string;
        uploadedAt: string;
        downloadUrl: string;
      };
      signature?: {
        fileKey: string;
        uploadedAt: string;
        downloadUrl: string;
      };
    };
    approvalHistory: {
      approvedBy?: string;
      approvalNotes?: string;
      rejectedBy?: string;
      rejectionReason?: string;
      rejectionNotes?: string;
      rejectedAt?: string;
    };
    credentialVerification: {
      status: string;
      verifiedAt?: string;
      expiresAt?: string;
      licenseState?: string;
      licenseNumber?: string;
      notes?: string;
      verifiedBy?: { email: string; name: string; role: string } | null;
    } | null;
    verificationHistory: Array<{
      action: string;
      previousStatus: string;
      newStatus: string;
      changedAt: string;
      reason?: string;
      notes?: string;
      changedBy?: { email: string; name: string; role: string } | null;
    }>;
  }>> {
    try {
      const response = await this.client.get<ApiResponse<any>>(
        `/account-managers/id-proofing/${physicianId}/documents`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physician documents'
      };
    }
  }

  // Account Manager - Get all physicians with ID proofing status
  async getAllPhysiciansIDProofing(params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    physicians: Array<{
      id: string;
      name: string;
      email: string;
      npiNumber: string;
      status: string;
      idProofingStatus: string;
      isVerified: boolean;
      submittedAt?: string;
      verifiedAt?: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.append('status', params.status);
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());

      const queryString = searchParams.toString();
      const url = `/account-managers/id-proofing/all${queryString ? `?${queryString}` : ''}`;

      const response = await this.client.get<ApiResponse<any>>(url);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get physicians list'
      };
    }
  }

  // Account Manager - Approve physician verification
  async approvePhysicianVerification(physicianId: string, notes?: string): Promise<ApiResponse<{
    physicianId: string;
    status: string;
    verifiedAt: string;
    approvedBy: string;
  }>> {
    try {
      const response = await this.client.put<ApiResponse<any>>(
        `/account-managers/id-proofing/${physicianId}/approve`,
        { notes }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to approve physician verification'
      };
    }
  }

  // Admin - Reject physician verification
  async rejectPhysicianVerification(physicianId: string, reason: string, notes?: string): Promise<ApiResponse<{
    physicianId: string;
    status: string;
    rejectedAt: string;
    rejectedBy: string;
    reason: string;
  }>> {
    try {
      const response = await this.client.put<ApiResponse<any>>(
        `/admin/id-proofing/${physicianId}/reject`,
        { reason, notes }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reject physician verification'
      };
    }
  }

  // Admin - Force a fresh CMS NPI registry lookup for a physician
  async refreshPhysicianNpiLookup(physicianId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.post<ApiResponse<any>>(
        `/account-managers/id-proofing/${physicianId}/refresh-npi`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to refresh NPI registry lookup'
      };
    }
  }

  // Admin - Verify physician credentials (Gate B: license confirmed with state board)
  async verifyPhysicianCredentials(
    physicianId: string,
    payload: { licenseState: string; licenseNumber: string; notes?: string }
  ): Promise<ApiResponse<{ physicianId: string; status: string }>> {
    try {
      const response = await this.client.post<ApiResponse<any>>(
        `/admin/credential-verification/${physicianId}/verify`,
        payload
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to verify physician credentials'
      };
    }
  }

  // Admin - Force verify physician (bypasses normal verification requirements)
  async forceVerifyPhysician(physicianId: string, notes?: string): Promise<ApiResponse<{
    physicianId: string;
    status: string;
    verifiedAt: string;
    verifiedBy: string;
    forceVerified: boolean;
  }>> {
    try {
      const response = await this.client.put<ApiResponse<any>>(
        `/admin/id-proofing/${physicianId}/force-verify`,
        { notes }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to force verify physician'
      };
    }
  }

  // Settings API methods
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<{}>> {
    try {
      const response = await this.post<ApiResponse<{}>>('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to change password'
      };
    }
  }

  async updateUserProfile(userId: string, profileData: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    clinicName?: string;
    npiNumber?: string;
    clinicAddress?: {
      street?: string;
      street2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/users/${userId}/profile`, profileData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update profile'
      };
    }
  }

  async inviteUser(userData: {
    email: string;
    // Optional for physician invites (email-only); required for other roles.
    firstName?: string;
    lastName?: string;
    role: string;
    phone?: string;
    companyName?: string;
    catalogIds?: string[];
    distributorId?: string; // Account Manager ID for physician assignments
    // Pre-fill fields for physician invitations
    clinicName?: string;
    driversLicense?: string;
    clinicAddress?: {
      street?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
    // Telemed-only clinic (admin + telemed permission only): no NPI; network prescribes.
    telemedOnly?: boolean;
    telemedNetworkId?: string;
    // Commission mode (admin-only, supervisor / AM invites only)
    percentMarkupEnabled?: boolean;
    percentMarkupPercent?: number;
    // Internal sales-rep mode (admin-only, AM / distributor invites only)
    salesRepEnabled?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/users/invitations', userData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to invite user'
      };
    }
  }

  async getInvitations(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.get<ApiResponse<any[]>>('/users/invitations');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch invitations'
      };
    }
  }

  async revokeInvitation(invitationId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.delete<ApiResponse<any>>(`/users/invitations/${invitationId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to revoke invitation'
      };
    }
  }

  async resendInvitation(invitationId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/users/invitations/${invitationId}/resend`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend invitation'
      };
    }
  }

  // Catalog Methods
  async getCatalogs(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.get<ApiResponse<any>>('/catalogs/assigned');
      return {
        success: response.success,
        data: response.data?.catalogs || []
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch catalogs'
      };
    }
  }

  // Notification Preferences Methods
  // In-app notification feed (dashboard bell)
  async getNotifications(opts?: { unreadOnly?: boolean; limit?: number }): Promise<ApiResponse<{ items: any[]; unreadCount: number }>> {
    try {
      const params = new URLSearchParams();
      if (opts?.unreadOnly) params.append('unreadOnly', 'true');
      if (opts?.limit) params.append('limit', String(opts.limit));
      const qs = params.toString();
      return await this.get<ApiResponse<{ items: any[]; unreadCount: number }>>(`/notifications${qs ? `?${qs}` : ''}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch notifications' };
    }
  }

  async markNotificationRead(id: string): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>(`/notifications/${id}/read`, {});
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to mark notification read' };
    }
  }

  async markAllNotificationsRead(): Promise<ApiResponse<{ markedRead: number }>> {
    try {
      return await this.post<ApiResponse<{ markedRead: number }>>(`/notifications/read-all`, {});
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to mark all read' };
    }
  }

  async getNotificationPreferences(): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>('/notification-preferences');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch notification preferences'
      };
    }
  }

  async updateNotificationPreferences(preferences: {
    receiveEmails?: boolean;
    receiveTexts?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>('/notification-preferences', preferences);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update notification preferences'
      };
    }
  }

  async testNotifications(): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/notification-preferences/test', {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send test notification'
      };
    }
  }

  // Payout Management Methods
  async getPayouts(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.get<ApiResponse<any[]>>('/payouts');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch payouts'
      };
    }
  }

  async createPayout(payoutData: {
    recipientId: string;
    startDate: string;
    endDate: string;
    amount: number;
    wireNumber?: string;
    description?: string;
    notesForRecipient?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/payouts', payoutData);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create payout'
      };
    }
  }

  async markPayoutAsSent(payoutId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/payouts/${payoutId}/mark-sent`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark payout as sent'
      };
    }
  }

  async confirmPayout(payoutId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/payouts/${payoutId}/confirm`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to confirm payout'
      };
    }
  }

  async getRevenuePreview(params: {
    recipientId: string;
    startDate: string;
    endDate: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/payouts/revenue-preview?${new URLSearchParams(params).toString()}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to calculate revenue preview'
      };
    }
  }

  async getBulkPayoutPreview(params: {
    startDate: string;
    endDate: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/payouts/bulk-preview?${new URLSearchParams(params).toString()}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load bulk payout preview'
      };
    }
  }

  async bulkCreatePayouts(body: {
    startDate: string;
    endDate: string;
    recipientIds: string[];
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/payouts/bulk-create', body);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to bulk-create payouts'
      };
    }
  }

  // ==========================================================================
  // PayQuicker payout rail
  // ==========================================================================

  /**
   * Send a payout over the PayQuicker rail.
   *
   * Unlike the other helpers here, this deliberately surfaces `outcome`,
   * `retryable` and the HTTP status, because the caller MUST distinguish them:
   *  - 200 sent / already_sent  → instructed (not settled)
   *  - 409 ineligible           → preconditions unmet or a concurrent send won
   *  - 400 rejected             → nothing created; safe to retry
   *  - 402 failed               → instruction dead; earnings released, new payout needed
   *  - 202 needs_reconciliation → indeterminate; MUST NOT offer a retry
   */
  async sendPayoutViaPayQuicker(payoutId: string): Promise<ApiResponse<any> & {
    outcome?: string;
    retryable?: boolean;
    status?: number;
  }> {
    try {
      const response = await this.post<any>(`/payouts/${payoutId}/send-payquicker`, {});
      return { ...response, status: 200 };
    } catch (error: any) {
      const status = error.response?.status;
      const body = error.response?.data || {};
      return {
        success: false,
        error: body.error || 'Failed to send payout via PayQuicker',
        outcome: body.outcome,
        // Default to NOT retryable: an unknown failure must never invite a
        // second send attempt.
        retryable: body.retryable === true,
        data: body.data,
        status
      };
    }
  }

  /** Invite a payout recipient to PayQuicker (they complete KYC on PayQuicker's side). */
  async invitePayQuickerRecipient(userId: string): Promise<ApiResponse<any> & {
    outcome?: string;
    status?: number;
  }> {
    try {
      const response = await this.post<any>(`/payouts/recipients/${userId}/payquicker-invite`, {});
      return { ...response, status: 200 };
    } catch (error: any) {
      const body = error.response?.data || {};
      return {
        success: false,
        error: body.error || 'Failed to invite recipient to PayQuicker',
        outcome: body.outcome,
        status: error.response?.status
      };
    }
  }

  /** Payouts on the rail that need a human: unresolved, held for review, or failed. */
  async getRailPayoutsNeedingAttention(limit = 100): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.get<ApiResponse<any[]>>(`/payouts/rail-attention?limit=${limit}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load payout rail status'
      };
    }
  }

  /** Trigger an on-demand reconciliation sweep of unresolved rail payouts. */
  async reconcileRailPayouts(body: { olderThanMinutes?: number; limit?: number } = {}): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/payouts/reconcile-rail', body);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reconcile payouts'
      };
    }
  }

  // ==========================================================================
  // Commission Override Methods (admin account tags)
  // ==========================================================================

  async getCommissionOverrides(filter: {
    status?: string; kind?: string; scope?: string; source?: string;
    accountId?: string; supervisorId?: string; recipientId?: string;
  } = {}): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams(
        Object.entries(filter).filter(([, v]) => !!v) as [string, string][]
      ).toString();
      return await this.get<ApiResponse<any>>(`/commission-overrides${params ? `?${params}` : ''}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load overrides' };
    }
  }

  async getAccountCommissionOverrides(accountId: string): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/commission-overrides/account/${accountId}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load account overrides' };
    }
  }

  async getSupervisorCommissionOverrides(supervisorId: string): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/commission-overrides/supervisor/${supervisorId}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load supervisor overrides' };
    }
  }

  async getCommissionOverrideRecipients(search?: string): Promise<ApiResponse<any>> {
    try {
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      return await this.get<ApiResponse<any>>(`/commission-overrides/recipients${q}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load recipients' };
    }
  }

  async createCommissionOverride(body: {
    scope?: 'account' | 'supervisor';
    source?: 'am' | 'house';
    accountId?: string; supervisorId?: string;
    recipientId: string; kind: 'permanent' | 'temporary';
    rate: number; startDate?: string; endDate?: string; note?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>('/commission-overrides', body);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to create override' };
    }
  }

  async endCommissionOverride(id: string): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>(`/commission-overrides/${id}/end`, {});
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to end override' };
    }
  }

  async cancelCommissionOverride(id: string, reason?: string): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>(`/commission-overrides/${id}/cancel`, { reason });
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to cancel override' };
    }
  }

  // ---- Clinic deals (negotiated per-product pricing) ----

  async getClinicDeals(filter: {
    status?: string; accountId?: string; productId?: string;
  } = {}): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set('status', filter.status);
      if (filter.accountId) params.set('accountId', filter.accountId);
      if (filter.productId) params.set('productId', filter.productId);
      const qs = params.toString();
      return await this.get<ApiResponse<any>>(`/clinic-deals${qs ? `?${qs}` : ''}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load deals' };
    }
  }

  async getAccountClinicDeals(accountId: string): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/clinic-deals/account/${accountId}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load clinic deals' };
    }
  }

  /** Live house-keep math for the create form. Never writes. */
  async previewClinicDeal(body: {
    accountId: string;
    products: Array<{ productId: string; finalPrice: number; amCut: number; supervisorCut?: number }>;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>('/clinic-deals/preview', body);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to preview deal' };
    }
  }

  /** Active deals that would go house-keep-negative at a proposed basePrice. */
  async getClinicDealExposure(productId: string, newBasePrice: number): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(
        `/clinic-deals/exposure/${productId}?newBasePrice=${encodeURIComponent(String(newBasePrice))}`
      );
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to check deal exposure' };
    }
  }

  async createClinicDeal(body: {
    accountId: string;
    name: string;
    products: Array<{ productId: string; finalPrice: number; amCut: number; supervisorCut?: number }>;
    startDate?: string;
    endDate?: string;
    note?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>('/clinic-deals', body);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to create deal' };
    }
  }

  async endClinicDeal(id: string): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>(`/clinic-deals/${id}/end`, {});
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to end deal' };
    }
  }

  // --- Facility shipments (per-parcel carrier billing) ---

  async getFacilityShipments(filter: {
    status?: string; physicianId?: string; reconciliationRequired?: boolean;
    search?: string; startDate?: string; endDate?: string;
    page?: number; limit?: number;
  } = {}): Promise<ApiResponse<any>> {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set('status', filter.status);
      if (filter.physicianId) params.set('physicianId', filter.physicianId);
      if (filter.reconciliationRequired) params.set('reconciliationRequired', 'true');
      if (filter.search) params.set('search', filter.search);
      if (filter.startDate) params.set('startDate', filter.startDate);
      if (filter.endDate) params.set('endDate', filter.endDate);
      if (filter.page) params.set('page', String(filter.page));
      if (filter.limit) params.set('limit', String(filter.limit));
      const qs = params.toString();
      return await this.get<ApiResponse<any>>(`/admin/facility-shipments${qs ? `?${qs}` : ''}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load shipments' };
    }
  }

  async getFacilityShipment(id: string): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/admin/facility-shipments/${id}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load shipment' };
    }
  }

  async retryFacilityShipmentCharge(id: string): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>(`/admin/facility-shipments/${id}/retry-charge`, {});
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to retry charge' };
    }
  }

  async resolveFacilityShipment(id: string, note: string): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>(`/admin/facility-shipments/${id}/resolve`, { note });
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to resolve shipment' };
    }
  }

  /** Parcel detail behind an item-less facility_shipping invoice. */
  async getShipmentForInvoice(invoiceId: string): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/invoices/${invoiceId}/shipment`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load shipment detail' };
    }
  }

  // --- Telemed Contract + BAA (network clinics) ---

  async getPhysicianAgreement(physicianId: string): Promise<ApiResponse<any>> {
    try {
      return await this.get<ApiResponse<any>>(`/physicians/${physicianId}/agreement`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load agreement status' };
    }
  }

  /**
   * Sign or re-sign the agreement on its own. Unlike the onboarding submit this
   * does not move idProofingStatus, so an already-approved clinic stays verified.
   */
  async signPhysicianAgreement(
    physicianId: string,
    signatureBlob: Blob,
    agreementVersion: string
  ): Promise<ApiResponse<any>> {
    try {
      const signatureData: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(signatureBlob);
      });
      return await this.post<ApiResponse<any>>(`/physicians/${physicianId}/agreement/sign`, {
        signatureData,
        agreementVersion,
        agreementAccepted: true
      });
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to sign agreement' };
    }
  }

  async createCommissionOverridePartner(body: {
    firstName: string; lastName: string; email: string;
    companyName?: string; phone?: string; paymentInstructions?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.post<ApiResponse<any>>('/commission-overrides/partners', body);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to create partner' };
    }
  }

  async getPayoutInvoiceBreakdown(payoutId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/payouts/${payoutId}/invoice-breakdown`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get payout invoice breakdown'
      };
    }
  }

  async downloadPayoutCSV(payoutId: string): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseURL}/payouts/${payoutId}/export-csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }

      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'payout-export.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error downloading payout CSV:', error);
      throw error;
    }
  }

  // Admin All Patients
  async getAdminPatients(params: { q?: string; physicianId?: string; status?: string; state?: string; page?: number; limit?: number }): Promise<ApiResponse<AdminPatientSearchResponse>> {
    try {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') query.append(key, String(value));
      }
      return await this.get<ApiResponse<AdminPatientSearchResponse>>(`/admin/patients?${query.toString()}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to search patients' };
    }
  }

  async getAdminPatient(patientId: string): Promise<ApiResponse<AdminPatientDetail>> {
    try {
      return await this.get<ApiResponse<AdminPatientDetail>>(`/admin/patients/${patientId}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load patient' };
    }
  }

  // Impersonation Methods
  async startImpersonation(targetUserId: string, reason?: string): Promise<ApiResponse<{ token: string; sessionId: string; expiresAt: string }>> {
    try {
      const response = await this.post<ApiResponse<{ token: string; sessionId: string; expiresAt: string }>>('/impersonation/start', {
        targetUserId,
        reason
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to start impersonation'
      };
    }
  }

  async stopImpersonation(): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.post<ApiResponse<{ message: string }>>('/impersonation/stop');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to stop impersonation'
      };
    }
  }

  async getImpersonationStatus(): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>('/impersonation/status');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get impersonation status'
      };
    }
  }

  async getActiveSessions(): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.get<ApiResponse<any[]>>('/impersonation/sessions');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get active sessions'
      };
    }
  }

  async getImpersonationHistory(limit: number = 50): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.get<ApiResponse<any[]>>(`/impersonation/history?limit=${limit}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get impersonation history'
      };
    }
  }

  async terminateImpersonationSession(sessionId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.delete<ApiResponse<{ message: string }>>(`/impersonation/sessions/${sessionId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to terminate session'
      };
    }
  }

  async updateImpersonationActivity(): Promise<ApiResponse<{ updated: boolean }>> {
    try {
      const response = await this.post<ApiResponse<{ updated: boolean }>>('/impersonation/activity', {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update activity'
      };
    }
  }

  async checkImpersonationSessionActive(): Promise<ApiResponse<{ isActive: boolean; isImpersonating: boolean; sessionExpired?: boolean }>> {
    try {
      const response = await this.get<ApiResponse<{ isActive: boolean; isImpersonating: boolean; sessionExpired?: boolean }>>('/impersonation/check-active');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to check session'
      };
    }
  }

  async getDistributors(params?: { page?: number; limit?: number; status?: string; search?: string }): Promise<ApiResponse<{ data: any[]; pagination: { page: number; limit: number; totalCount: number; totalPages: number; hasMore: boolean } }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status) queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);

      const queryString = queryParams.toString();
      const url = queryString ? `/users/distributors?${queryString}` : '/users/distributors';

      const response = await this.get<ApiResponse<{ data: any[]; pagination: { page: number; limit: number; totalCount: number; totalPages: number; hasMore: boolean } }>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch distributors'
      };
    }
  }

  /**
   * Fetch all distributors by iterating through all pages
   */
  async getAllDistributors(): Promise<ApiResponse<any[]>> {
    try {
      const allDistributors: any[] = [];
      let page = 1;
      let hasMore = true;
      const limit = 100; // Fetch 100 at a time for efficiency

      while (hasMore) {
        const response = await this.getDistributors({ page, limit });

        if (!response.success || !response.data) {
          return {
            success: false,
            error: response.error || 'Failed to fetch distributors'
          };
        }

        allDistributors.push(...response.data.data);
        hasMore = response.data.pagination.hasMore;
        page++;
      }

      return {
        success: true,
        data: allDistributors
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch all distributors'
      };
    }
  }

  // Product Requests endpoints
  async createProductRequest(data: {
    requestTitle: string;
    requestDescription: string;
    requestType?: 'new_product' | 'product_variant' | 'other';
    suggestedDetails?: {
      drugName?: string;
      concentration?: string;
      format?: string;
      size?: string;
      categories?: string[];
    };
    desiredPrice?: number;
    quantityNeeded?: number;
    relatedProductId?: string;
  }): Promise<any> {
    try {
      const response = await this.post<any>('/product-requests', data);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create product request');
    }
  }

  async getProductRequests(params?: {
    status?: string;
    requestType?: string;
  }): Promise<any[]> {
    try {
      const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
      const response = await this.get<any[]>(`/product-requests${queryString}`);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch product requests');
    }
  }

  async getProductRequest(id: string): Promise<any> {
    try {
      const response = await this.get<any>(`/product-requests/${id}`);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch product request');
    }
  }

  async addProductRequestComment(requestId: string, data: {
    message: string;
    isAdminNote?: boolean;
  }): Promise<any> {
    try {
      const response = await this.post<any>(`/product-requests/${requestId}/comments`, data);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to add comment');
    }
  }

  async updateProductRequestStatus(requestId: string, data: {
    status: 'pending' | 'in_review' | 'approved' | 'rejected';
    rejectionReason?: string;
    linkedProductId?: string;
  }): Promise<any> {
    try {
      const response = await this.client.patch(`/product-requests/${requestId}/status`, data);
      return response.data;
    } catch (error: any) {
      console.error('Update status error:', error.response?.data || error);
      throw new Error(error.response?.data?.message || 'Failed to update request status');
    }
  }

  // Product Request Subscription endpoints
  async subscribeToProductRequests(): Promise<any> {
    try {
      const response = await this.post<any>('/product-requests/subscriptions', {});
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to subscribe to product requests');
    }
  }

  async unsubscribeFromProductRequests(): Promise<any> {
    try {
      const response = await this.client.delete('/product-requests/subscriptions');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to unsubscribe from product requests');
    }
  }

  async getProductRequestSubscriptionStatus(requestId?: string): Promise<{ isSubscribed: boolean; subscription: any }> {
    try {
      const url = requestId 
        ? `/product-requests/${requestId}/subscriptions/status`
        : '/product-requests/subscriptions/status';
      const response = await this.get<any>(url);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subscription status');
    }
  }

  // Per-request subscription methods
  async subscribeToProductRequest(requestId: string): Promise<any> {
    try {
      const response = await this.post<any>(`/product-requests/${requestId}/subscriptions`, {});
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to subscribe to product request');
    }
  }

  async unsubscribeFromProductRequest(requestId: string): Promise<any> {
    try {
      const response = await this.client.delete(`/product-requests/${requestId}/subscriptions`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to unsubscribe from product request');
    }
  }

  // ============================================================
  // Admin Orders Management - Unified view of all orders
  // ============================================================

  // Unified order types for frontend
  static readonly ORDER_TYPES = {
    PATIENT: 'patient' as const,
    BULK: 'bulk' as const,
    ALL: 'all' as const
  };

  async getAdminOrders(params?: {
    page?: number;
    limit?: number;
    orderType?: 'patient' | 'bulk' | 'all';
    status?: string;
    invoiceStatus?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{
    data: Array<{
      _id: string;
      orderNumber: string;
      orderType: 'patient' | 'bulk';
      status: string;
      totalAmount: number;
      itemCount: number;
      physician: { _id: string; name: string } | null;
      accountManager?: { _id: string; name: string } | null;
      patient?: { _id: string; name: string } | null;
      fulfillmentType?: 'pickup' | 'bulk_ship' | 'patient_ship';
      shippingAddress?: { street?: string; city?: string; state?: string; zipCode?: string } | null;
      createdAt: string;
      shippedAt?: string;
      deliveredAt?: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      totalCount: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    counts: {
      patient: number;
      bulk: number;
    };
  }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.orderType) queryParams.append('orderType', params.orderType);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.invoiceStatus) queryParams.append('invoiceStatus', params.invoiceStatus);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const url = queryParams.toString() ? `/admin/orders?${queryParams.toString()}` : '/admin/orders';
      const response = await this.get<any>(url);
      
      return {
        success: response.success,
        data: {
          data: response.data || [],
          pagination: response.pagination || {
            page: 1,
            limit: 20,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false
          },
          counts: response.counts || { patient: 0, bulk: 0 }
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch admin orders'
      };
    }
  }

  async getAdminOrderDetail(orderId: string, orderType: 'patient' | 'bulk'): Promise<ApiResponse<{
    _id: string;
    orderNumber: string;
    orderType: 'patient' | 'bulk';
    status: string;
    telemedNetworkFee?: number;
    totalAmount: number;
    itemCount: number;
    physician: { _id: string; name: string } | null;
    accountManager?: { _id: string; name: string } | null;
    patient?: { _id: string; name: string } | null;
    fulfillmentType?: 'pickup' | 'bulk_ship' | 'patient_ship';
    shippingAddress?: { street?: string; city?: string; state?: string; zipCode?: string } | null;
    items: Array<{
      productId: string;
      productName: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
    }>;
    paymentDetails?: {
      method: string;
      status: string;
      amount: number;
    };
    trackingInfo?: {
      carrier?: string;
      trackingNumber?: string;
      trackingUrl?: string;
      addedBy?: { _id: string; name: string };
      addedAt?: string;
    };
    invoiceId?: string;
    invoiceNumber?: string;
    invoiceStatus?: string;
    createdAt: string;
    submittedAt?: string;
    paidAt?: string;
    shippedAt?: string;
    deliveredAt?: string;
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/admin/orders/${orderId}?orderType=${orderType}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch order details'
      };
    }
  }

  async updateAdminOrderStatus(
    orderId: string,
    orderType: 'patient' | 'bulk',
    newStatus: string,
    trackingInfo?: {
      carrier?: string;
      trackingNumber?: string;
      trackingUrl?: string;
    }
  ): Promise<ApiResponse<{
    _id: string;
    orderNumber: string;
    status: string;
  }>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/admin/orders/${orderId}/status`, {
        orderType,
        newStatus,
        trackingInfo
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update order status'
      };
    }
  }

  // Re-transmit order to pharmacy (admin only)
  async retransmitOrderToPharmacy(
    orderId: string,
    orderType: 'patient' | 'bulk'
  ): Promise<ApiResponse<{
    orderId: string;
    orderNumber: string;
    pharmacyOrderId?: string;
    pharmacyOrderIds?: number[];
    scriptsSubmitted?: number;
    totalSubmitted?: number;
  }>> {
    try {
      const response = await this.post<ApiResponse<any>>(
        `/admin/orders/${orderId}/retransmit-pharmacy?type=${orderType}`,
        {}
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to re-transmit order to pharmacy'
      };
    }
  }

  // ============================================================
  // PHARMACY CONFIG ENDPOINTS (Admin only)
  // ============================================================

  async getPharmacyConfigs(): Promise<ApiResponse<Array<{
    _id: string;
    name: string;
    ncpdpId: string;
    stateRules: string[];
    productRules: Array<{ _id: string; drugName?: string; displayName?: string }>;
    isDefault: boolean;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }>>> {
    try {
      const response = await this.get<ApiResponse<any>>('/admin/pharmacy-configs');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch pharmacy configurations'
      };
    }
  }

  async createPharmacyConfig(data: {
    name: string;
    ncpdpId: string;
    stateRules?: string[];
    productRules?: string[];
    isDefault?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/admin/pharmacy-configs', data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create pharmacy configuration'
      };
    }
  }

  async updatePharmacyConfig(
    configId: string,
    data: {
      name?: string;
      ncpdpId?: string;
      stateRules?: string[];
      productRules?: string[];
      isDefault?: boolean;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/admin/pharmacy-configs/${configId}`, data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update pharmacy configuration'
      };
    }
  }

  async deletePharmacyConfig(configId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.delete<ApiResponse<any>>(`/admin/pharmacy-configs/${configId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete pharmacy configuration'
      };
    }
  }

  async testPharmacyRouting(params: {
    state?: string;
    productIds?: string;
  }): Promise<ApiResponse<{
    resolvedNcpdpId: string;
    matchedBy: 'state' | 'product' | 'default' | 'env';
    inputState: string | null;
    inputProductIds: string[];
  }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.state) queryParams.append('state', params.state);
      if (params.productIds) queryParams.append('productIds', params.productIds);

      const url = `/admin/pharmacy-configs/test${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.get<ApiResponse<any>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to test pharmacy routing'
      };
    }
  }

  // ============ Bulk Orders ============

  /**
   * One page of bulk orders (default and max 100), searched and filtered on the
   * server. `pagination` has the real total; `statusCounts` counts every order
   * in scope matching `search`, by status, for filter chips.
   */
  async getBulkOrders(filters?: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<ApiResponse<any[]> & { pagination?: any; statusCounts?: Record<string, number> }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.search?.trim()) queryParams.append('search', filters.search.trim());
      if (filters?.page) queryParams.append('page', String(filters.page));
      if (filters?.limit) queryParams.append('limit', String(filters.limit));

      const url = `/bulk-orders${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.get<ApiResponse<any[]> & { pagination?: any; statusCounts?: Record<string, number> }>(url, { signal: filters?.signal });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch bulk orders'
      };
    }
  }

  async getBulkOrder(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/bulk-orders/${id}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch bulk order'
      };
    }
  }

  async createBulkOrder(data: {
    physicianId: string;
    items: { productId: string; quantity: number }[];
    fulfillmentDetails: {
      type: 'pickup' | 'bulk_ship' | 'patient_ship';
      address?: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
      };
      patientIds?: string[];
      notes?: string;
    };
    requiresPhysicianApproval?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/bulk-orders', data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create bulk order'
      };
    }
  }

  async submitBulkOrder(id: string): Promise<ApiResponse<{ bulkOrder: any; invoice: any | null; message?: string }>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/bulk-orders/${id}/submit`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit bulk order'
      };
    }
  }

  async approveBulkOrder(id: string): Promise<ApiResponse<{ bulkOrder: any; invoice: any; message?: string }>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/bulk-orders/${id}/approve`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to approve bulk order'
      };
    }
  }

  async rejectBulkOrder(id: string, reason?: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/bulk-orders/${id}/reject`, { reason });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reject bulk order'
      };
    }
  }

  async updateBulkOrder(id: string, data: {
    items?: { productId: string; quantity: number }[];
    fulfillmentDetails?: {
      type: 'pickup' | 'bulk_ship' | 'patient_ship';
      address?: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
      };
      notes?: string;
    };
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/bulk-orders/${id}`, data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update bulk order'
      };
    }
  }

  // ============================================================
  // ACCOUNTS RECEIVABLE (AR) ENDPOINTS (Admin only)
  // ============================================================

  async getARSummary(filters?: {
    distributorId?: string;
    overdueOnly?: boolean;
    search?: string;
  }): Promise<ApiResponse<{
    summary: Array<{
      physicianId: string;
      physicianName: string;
      clinicName?: string;
      distributorId?: string;
      distributorName?: string;
      creditLimit: number | null;
      outstandingBalance: number;
      availableCredit: number | null;
      aging: {
        current: number;
        days1to30: number;
        days31to60: number;
        days61to90: number;
        days90plus: number;
        total: number;
      };
      invoiceCount: number;
      oldestOverdueDate?: string;
    }>;
    totals: {
      current: number;
      days1to30: number;
      days31to60: number;
      days61to90: number;
      days90plus: number;
      total: number;
    };
  }>> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.distributorId) queryParams.append('distributorId', filters.distributorId);
      if (filters?.overdueOnly) queryParams.append('overdueOnly', 'true');
      if (filters?.search) queryParams.append('search', filters.search);

      const url = queryParams.toString() ? `/ar/summary?${queryParams.toString()}` : '/ar/summary';
      const response = await this.get<ApiResponse<any>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch AR summary'
      };
    }
  }

  async getPhysicianARDetail(physicianId: string): Promise<ApiResponse<{
    summary: {
      physicianId: string;
      physicianName: string;
      clinicName?: string;
      distributorId?: string;
      distributorName?: string;
      creditLimit: number | null;
      outstandingBalance: number;
      availableCredit: number | null;
      aging: {
        current: number;
        days1to30: number;
        days31to60: number;
        days61to90: number;
        days90plus: number;
        total: number;
      };
      invoiceCount: number;
      statementCount: number;
      statementTotal: number;
      oldestOverdueDate?: string;
    };
    invoices: any[];
    statements: any[];
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/ar/physician/${physicianId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch physician AR detail'
      };
    }
  }

  async getARByDistributor(): Promise<ApiResponse<Array<{
    distributorId: string;
    distributorName: string;
    totalOutstanding: number;
    physicianCount: number;
  }>>> {
    try {
      const response = await this.get<ApiResponse<any>>('/ar/by-distributor');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch AR by distributor'
      };
    }
  }

  async exportARToCSV(filters?: {
    distributorId?: string;
    overdueOnly?: boolean;
  }): Promise<string> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.distributorId) queryParams.append('distributorId', filters.distributorId);
      if (filters?.overdueOnly) queryParams.append('overdueOnly', 'true');

      const url = queryParams.toString() ? `/ar/export?${queryParams.toString()}` : '/ar/export';
      const response = await this.client.get(url, {
        responseType: 'text'
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to export AR data');
    }
  }

  async getCreditStatus(physicianId: string): Promise<ApiResponse<{
    creditLimit: number | null;
    outstandingBalance: number;
    availableCredit: number | null;
    isOverLimit: boolean;
    invoiceCount: number;
  }>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/ar/credit-status/${physicianId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch credit status'
      };
    }
  }

  async checkCreditForOrder(physicianId: string, orderAmount: number): Promise<ApiResponse<{
    allowed: boolean;
    warning: boolean;
    message?: string;
  }>> {
    try {
      const response = await this.post<ApiResponse<any>>('/ar/check-credit', {
        physicianId,
        orderAmount
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to check credit'
      };
    }
  }

  // Update physician billing configuration (admin only)
  async updatePhysicianBillingConfig(physicianId: string, config: {
    customDomainEnabled?: boolean;
    creditLimit?: number | null;
    paymentTerms?: 'immediate' | 'net7' | 'net15' | 'net30';
    arEnabled?: boolean;
    patientPayEnabled?: boolean;
    facilityOnly?: boolean;
    directToPatientEnabled?: boolean;
    membershipEnabled?: boolean;
    volumeRewardsEnabled?: boolean;
    /** Pharmacy account type — requires its own NPI, excludes isVet. */
    isPharmacy?: boolean;
    /** Lab testing access. Off by default; granted per account by an admin. */
    labsEnabled?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/users/${physicianId}/billing-config`, config);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update billing configuration'
      };
    }
  }

  // --- DTP auto-import (signed-link patient sessions) — admin only ---

  async getDtpAutoImportStatus(physicianId: string): Promise<ApiResponse<{
    enabled: boolean;
    hasSecret: boolean;
    createdAt?: string;
  }>> {
    return this.get<ApiResponse<any>>(`/users/${physicianId}/dtp-auto-import`);
  }

  // Generate/rotate the clinic's HMAC secret (returned ONCE) and enable
  // auto-import. Rotating invalidates every outstanding signed link.
  async rotateDtpAutoImportSecret(physicianId: string): Promise<ApiResponse<{
    secret: string;
    createdAt: string;
    algorithm: string;
    signedParamKeys: string[];
    sampleLinkPath: string;
  }>> {
    return this.post<ApiResponse<any>>(`/users/${physicianId}/dtp-auto-import/rotate`, {});
  }

  async setDtpAutoImportEnabled(physicianId: string, enabled: boolean): Promise<ApiResponse<{ enabled: boolean }>> {
    return this.put<ApiResponse<any>>(`/users/${physicianId}/dtp-auto-import`, { enabled });
  }

  // ---- Telemed networks (admin only) ----
  async getTelemedNetworks(): Promise<ApiResponse<TelemedNetwork[]>> {
    return this.get<ApiResponse<TelemedNetwork[]>>('/telemed-networks');
  }

  async getTelemedNetwork(id: string): Promise<ApiResponse<TelemedNetwork & { clinicCount: number }>> {
    return this.get<ApiResponse<any>>(`/telemed-networks/${id}`);
  }

  async createTelemedNetwork(body: Partial<TelemedNetwork>): Promise<ApiResponse<TelemedNetwork>> {
    return this.post<ApiResponse<TelemedNetwork>>('/telemed-networks', body);
  }

  async updateTelemedNetwork(id: string, body: Partial<TelemedNetwork>): Promise<ApiResponse<TelemedNetwork>> {
    return this.put<ApiResponse<TelemedNetwork>>(`/telemed-networks/${id}`, body);
  }

  async deleteTelemedNetwork(id: string): Promise<ApiResponse<void>> {
    try {
      return await this.delete<ApiResponse<void>>(`/telemed-networks/${id}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to delete network' } as any;
    }
  }

  // Assign (or clear with null) the telemed network a clinic account uses, and
  // optionally mark it telemed-only (no clinic NPI; the network prescribes).
  async assignTelemedNetwork(
    physicianId: string,
    telemedNetworkId: string | null,
    telemedOnly?: boolean,
    useNetworkForUncoveredStates?: boolean
  ): Promise<ApiResponse<{ telemedNetworkId: string | null; telemedOnly?: boolean }>> {
    try {
      return await this.put<ApiResponse<any>>(`/users/physicians/${physicianId}/telemed-network`, {
        telemedNetworkId,
        ...(typeof telemedOnly === 'boolean' && { telemedOnly }),
        ...(typeof useNetworkForUncoveredStates === 'boolean' && { useNetworkForUncoveredStates })
      });
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to assign network' } as any;
    }
  }

  // Update percent-markup mode for a supervisor or AM (admin only).
  // When enabling, the user's existing catalogs must already use pass-through
  // pricing — the backend returns 400 with offending catalog IDs otherwise.
  async updatePercentMarkupConfig(userId: string, body: {
    enabled: boolean;
    percent?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>(`/users/${userId}/percent-markup`, body);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update percent markup configuration',
        // surface the offending list when the backend returns it
        ...(error.response?.data?.offending ? { offending: error.response.data.offending } : {})
      } as any;
    }
  }

  /** Admin: enable/disable internal sales-rep mode for an AM (distributor). */
  async updateSalesRepConfig(userId: string, body: {
    enabled: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.put<ApiResponse<any>>(`/users/${userId}/sales-rep`, body);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update sales-rep configuration'
      } as any;
    }
  }

  /** Admin: designate a catalog as the sales-rep catalog and set per-product
   *  list price + discount tiers. */
  async updateSalesRepCatalogConfig(catalogId: string, body: {
    isSalesRepCatalog?: boolean;
    tierPercents?: number[];
  }): Promise<ApiResponse<any>> {
    try {
      return await this.put<ApiResponse<any>>(`/catalogs/${catalogId}/sales-rep-config`, body);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update sales-rep catalog configuration'
      } as any;
    }
  }

  // ============================================================
  // PATIENT PAY MARKUP METHODS
  // ============================================================

  async getPatientPayMarkupDefaults(): Promise<ApiResponse<{ defaults: Record<string, number>; hiddenProducts: string[] }>> {
    try {
      const response = await this.get<ApiResponse<{ defaults: Record<string, number>; hiddenProducts: string[] }>>('/users/me/patient-pay-markup');
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get markup defaults'
      };
    }
  }

  async updatePatientPayMarkupDefaults(defaults: Record<string, number>, hiddenProducts?: string[]): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>('/users/me/patient-pay-markup', { defaults, hiddenProducts });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update markup defaults'
      };
    }
  }

  // ============================================================
  // SAVED PRODUCT SIGS (physician's own per-product sig presets)
  // ============================================================

  async getMyProductSigs(): Promise<ApiResponse<{ sigs: Record<string, string[]> }>> {
    try {
      return await this.get<ApiResponse<{ sigs: Record<string, string[]> }>>('/users/me/product-sigs');
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get saved sigs'
      };
    }
  }

  async updateMyProductSigs(sigs: Record<string, string[]>): Promise<ApiResponse<any>> {
    try {
      return await this.put<ApiResponse<any>>('/users/me/product-sigs', { sigs });
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update saved sigs'
      };
    }
  }

  async addMyProductSig(productId: string, sig: string): Promise<ApiResponse<{ sigs: string[] }>> {
    try {
      return await this.post<ApiResponse<{ sigs: string[] }>>(`/users/me/product-sigs/${productId}`, { sig });
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to save sig'
      };
    }
  }

  async updateFeaturedStoreProduct(productId: string | null): Promise<ApiResponse<{ featuredStoreProductId: string | null }>> {
    try {
      return await this.put<ApiResponse<{ featuredStoreProductId: string | null }>>(
        '/users/me/featured-product',
        { productId }
      );
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update featured product'
      };
    }
  }

  async updateStorefrontVariant(variant: 'lightweight' | 'educational' | 'custom'): Promise<ApiResponse<{ storefrontVariant: 'lightweight' | 'educational' | 'custom' }>> {
    try {
      return await this.put<ApiResponse<{ storefrontVariant: 'lightweight' | 'educational' | 'custom' }>>(
        '/users/me/storefront-variant',
        { variant }
      );
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update storefront variant'
      };
    }
  }

  async uploadCustomStoreAsset(file: File) {
    const body = new FormData(); body.append('file', file);
    return (await this.client.post('/users/me/custom-store/assets', body, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  }

  async customStoreAssetBlob(id: string): Promise<Blob> {
    return (await this.client.get('/users/me/custom-store/assets/' + encodeURIComponent(id), { responseType: 'blob' })).data;
  }

  async uploadStorefrontAsset(slot: 'logo' | 'darkLogo' | 'favicon', file: File) {
    const body = new FormData();
    body.append('file', file);
    const response = await this.client.post(`/users/me/storefront-assets/${slot}`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  }

  async updateStorefrontTheme(theme: {
    presetId?: string;
    accentColor?: string;
    headingFont?: string;
    bodyFont?: string;
  }): Promise<ApiResponse<{ storefrontTheme: any }>> {
    try {
      return await this.put<ApiResponse<{ storefrontTheme: any }>>(
        '/users/me/storefront-theme',
        theme
      );
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update storefront theme'
      };
    }
  }

  // ============================================================
  // STATEMENT METHODS (Consolidated Billing)
  // ============================================================

  async previewStatement(physicianId: string, dateRange?: { periodStart?: string; periodEnd?: string; dateField?: 'invoiceDate' | 'shippedDate' }): Promise<ApiResponse<{
    physicianId: string;
    physicianName: string;
    clinicName?: string;
    existingInvoices: Array<{
      invoiceId: string;
      invoiceNumber: string;
      totalAmount: number;
      dueDate: string;
      status: string;
      orderCount: number;
    }>;
    orphanOrders: Array<{
      orderId: string;
      orderNumber: string;
      totalAmount: number;
      status: string;
      createdAt: string;
    }>;
    aggregatedLineItems: Array<{
      productId: string;
      productName: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
    }>;
    subtotal: number;
    shippingTotal: number;
    telemedNetworkFeeTotal?: number;
    taxTotal?: number;
    totalAmount: number;
    invoiceCount: number;
    orderCount: number;
  }>> {
    try {
      const params = new URLSearchParams();
      if (dateRange?.periodStart) params.set('periodStart', dateRange.periodStart);
      if (dateRange?.periodEnd) params.set('periodEnd', dateRange.periodEnd);
      if (dateRange?.dateField) params.set('dateField', dateRange.dateField);
      const qs = params.toString();
      const response = await this.get<ApiResponse<any>>(`/statements/preview/${physicianId}${qs ? `?${qs}` : ''}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to preview statement'
      };
    }
  }

  async createStatement(data: {
    physicianId: string;
    periodStart?: string;
    periodEnd?: string;
    dateField?: 'invoiceDate' | 'shippedDate';
    paymentTerms?: 'immediate' | 'net7' | 'net15' | 'net30';
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>('/statements', data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create statement'
      };
    }
  }

  async getStatement(statementId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/statements/${statementId}`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch statement'
      };
    }
  }

  async getStatementInvoices(statementId: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await this.get<ApiResponse<any>>(`/statements/${statementId}/invoices`);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch statement invoices'
      };
    }
  }

  async getPhysicianStatements(physicianId: string, options?: {
    limit?: number;
    skip?: number;
  }): Promise<ApiResponse<{ statements: any[]; total: number }>> {
    try {
      const queryParams = new URLSearchParams();
      if (options?.limit) queryParams.append('limit', options.limit.toString());
      if (options?.skip) queryParams.append('skip', options.skip.toString());

      const url = queryParams.toString()
        ? `/statements/physician/${physicianId}?${queryParams.toString()}`
        : `/statements/physician/${physicianId}`;
      const response = await this.get<ApiResponse<any>>(url);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch physician statements'
      };
    }
  }

  async sendStatement(statementId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/statements/${statementId}/send`, {});
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send statement'
      };
    }
  }

  async recordStatementPayment(statementId: string, data: {
    wireTransferNumber?: string;
    paymentNotes?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/statements/${statementId}/record-payment`, data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to record statement payment'
      };
    }
  }

  async voidStatement(statementId: string, reason: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.post<ApiResponse<any>>(`/statements/${statementId}/void`, { reason });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to void statement'
      };
    }
  }
  // ==================== Team Members ====================

  async getTeamMembers(supervisorId: string): Promise<ApiResponse<any[]>> {
    try {
      const response: AxiosResponse = await this.client.get('/team-members', {
        params: { supervisorId }
      });
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch team members' };
    }
  }

  async createTeamMember(data: {
    email: string;
    supervisorUserId: string;
    firstName: string;
    lastName: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse = await this.client.post('/team-members', data);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to create team member' };
    }
  }

  async updateTeamMember(id: string, data: {
    firstName?: string;
    lastName?: string;
    status?: 'active' | 'suspended';
    password?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse = await this.client.put(`/team-members/${id}`, data);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to update team member' };
    }
  }

  async deleteTeamMember(id: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse = await this.client.delete(`/team-members/${id}`);
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to suspend team member' };
    }
  }

  // ===== Volume Rewards =====
  async getVolumeRewardsConfig(): Promise<ApiResponse<{ isActive: boolean; tiers: any[]; updatedAt: string | null }>> {
    try {
      const response = await this.get<ApiResponse<any>>('/admin/volume-rewards/config');
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load volume rewards config' };
    }
  }

  async updateVolumeRewardsConfig(body: {
    isActive: boolean;
    tiers: Array<{
      label: string;
      thresholdAmount: number;
      discountPercent: number;
      freeShipping?: boolean;
      priorityFulfillment?: boolean;
      perks?: string[];
    }>;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.put<ApiResponse<any>>('/admin/volume-rewards/config', body);
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to update volume rewards config' };
    }
  }

  async recomputeVolumeRewards(weekStart?: string): Promise<ApiResponse<{ processed: number; errors: number }>> {
    try {
      const response = await this.post<ApiResponse<any>>('/admin/volume-rewards/recompute', { weekStart });
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to recompute volume rewards' };
    }
  }

  // --- Lab results ---

  async getLabResults(patientId?: string): Promise<ApiResponse<LabResultSummary[]>> {
    try {
      const query = patientId ? `?patientId=${encodeURIComponent(patientId)}` : '';
      return await this.get<ApiResponse<LabResultSummary[]>>(`/lab-results${query}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load lab results' };
    }
  }

  async getLabResult(resultId: string): Promise<ApiResponse<LabResultDetail>> {
    try {
      return await this.get<ApiResponse<LabResultDetail>>(`/lab-results/${resultId}`);
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load lab result' };
    }
  }

  async getLabResultAttachmentUrl(
    resultId: string,
    index: number
  ): Promise<ApiResponse<{ url: string; fileName?: string }>> {
    try {
      return await this.get<ApiResponse<{ url: string; fileName?: string }>>(
        `/lab-results/${resultId}/attachments/${index}`
      );
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to open the report' };
    }
  }

  async getMyVolumeRewardsStatus(): Promise<ApiResponse<any>> {
    try {
      const response = await this.get<ApiResponse<any>>('/physicians/me/volume-rewards/status');
      return response;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || 'Failed to load volume rewards status' };
    }
  }
}

export const apiClient = new ApiClient();

// Export individual functions for easier testing and usage
export const inviteUser = (data: Parameters<ApiClient['inviteUser']>[0]) => apiClient.inviteUser(data);
export const getInvitations = () => apiClient.getInvitations();
export const revokeInvitation = (invitationId: string) => apiClient.revokeInvitation(invitationId);
export const resendInvitation = (invitationId: string) => apiClient.resendInvitation(invitationId);
export const getCatalogs = () => apiClient.getCatalogs();
export const updateCatalog = (catalogId: string, updates: Parameters<ApiClient['updateCatalog']>[1]) => apiClient.updateCatalog(catalogId, updates);

export default apiClient; 
